import {
  createClientId,
  CURTAIN_QUOTE_TYPE,
  GENERAL_QUOTE_TYPE,
} from "./generalQuotationUtils";

export const CRM_PIPELINE_TYPES = [CURTAIN_QUOTE_TYPE, GENERAL_QUOTE_TYPE];
export const CRM_CHECKLIST_MAX_DEPTH = 3;
export const CRM_REMINDER_TYPE_TASK_DUE = "task_due";
export const CRM_REMINDER_TYPE_INSTALLATION = "installation";
export const CRM_REMINDER_STATUS_ACTIVE = "active";
export const CRM_REMINDER_STATUS_DONE = "done";
export const CRM_REMINDER_STATUS_DISABLED = "disabled";
export const CRM_FOLLOW_UP_STATUS_PENDING = "pending";
export const CRM_FOLLOW_UP_STATUS_DONE = "done";
export const CRM_FOLLOW_UP_PRIORITY_LOW = "low";
export const CRM_FOLLOW_UP_PRIORITY_MEDIUM = "medium";
export const CRM_FOLLOW_UP_PRIORITY_HIGH = "high";
export const DEFAULT_CRM_REMINDER_TIME = "09:00";
export const CRM_REMINDER_REPEAT_OPTIONS = [
  { key: "10m", label: "Every 10 minutes", minutes: 10 },
  { key: "30m", label: "Every 30 minutes", minutes: 30 },
  { key: "1h", label: "Every 1 hour", minutes: 60 },
  { key: "daily", label: "Every day", minutes: 1440 },
];
export const CRM_FOLLOW_UP_REPEAT_OPTIONS = [
  { key: "none", label: "No repeat", minutes: 0 },
  { key: "10m", label: "Every 10 minutes", minutes: 10 },
  { key: "30m", label: "Every 30 minutes", minutes: 30 },
  { key: "1h", label: "Every 1 hour", minutes: 60 },
  { key: "daily", label: "Every day", minutes: 1440 },
  { key: "weekly", label: "Every week", minutes: 10080 },
];
export const CRM_FOLLOW_UP_PRIORITY_OPTIONS = [
  { key: CRM_FOLLOW_UP_PRIORITY_LOW, label: "Low" },
  { key: CRM_FOLLOW_UP_PRIORITY_MEDIUM, label: "Medium" },
  { key: CRM_FOLLOW_UP_PRIORITY_HIGH, label: "High" },
];

export const CRM_OPPORTUNITY_STATUS_OPEN = "open";
export const CRM_OPPORTUNITY_STATUS_WON = "won";
export const CRM_OPPORTUNITY_STATUS_LOST = "lost";
export const CRM_OPPORTUNITY_STATUS_NURTURE = "nurture";
export const CRM_OPPORTUNITY_STATUSES = [
  CRM_OPPORTUNITY_STATUS_OPEN,
  CRM_OPPORTUNITY_STATUS_WON,
  CRM_OPPORTUNITY_STATUS_LOST,
  CRM_OPPORTUNITY_STATUS_NURTURE,
];

export const CRM_OPPORTUNITY_STAGES = [
  { key: "new_lead", label: "New Lead" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "site_visit_scheduled", label: "Site Visit Scheduled" },
  { key: "measurement_completed", label: "Measurement Completed" },
  { key: "selection_design", label: "Selection / Design" },
  { key: "quotation_preparation", label: "Quotation Preparation" },
  { key: "quotation_sent", label: "Quotation Sent" },
  { key: "negotiation_decision", label: "Negotiation / Decision" },
  { key: "order_confirmed", label: "Order Confirmed" },
  { key: CRM_OPPORTUNITY_STATUS_WON, label: "Won" },
  { key: CRM_OPPORTUNITY_STATUS_LOST, label: "Lost" },
  { key: CRM_OPPORTUNITY_STATUS_NURTURE, label: "Nurture" },
];

const SALES_PIPELINE_STAGE_COLORS = {
  new_lead: "#2563eb",
  contacted: "#0ea5e9",
  qualified: "#14b8a6",
  site_visit_scheduled: "#06b6d4",
  measurement_completed: "#22c55e",
  selection_design: "#8b5cf6",
  quotation_preparation: "#6366f1",
  quotation_sent: "#f59e0b",
  negotiation_decision: "#f97316",
  order_confirmed: "#10b981",
  [CRM_OPPORTUNITY_STATUS_WON]: "#059669",
  [CRM_OPPORTUNITY_STATUS_LOST]: "#ef4444",
  [CRM_OPPORTUNITY_STATUS_NURTURE]: "#64748b",
};

export const DEFAULT_SALES_PIPELINE_STAGES = CRM_OPPORTUNITY_STAGES.map(
  (stage, index) => ({
    stageId: stage.key,
    id: stage.key,
    label: stage.label,
    order: (index + 1) * 10,
    active: true,
    color: SALES_PIPELINE_STAGE_COLORS[stage.key] || "#64748b",
    isWon: stage.key === CRM_OPPORTUNITY_STATUS_WON,
    isLost: stage.key === CRM_OPPORTUNITY_STATUS_LOST,
    isNurture: stage.key === CRM_OPPORTUNITY_STATUS_NURTURE,
    systemStage: true,
  })
);

export const DEFAULT_SALES_PIPELINE = {
  pipelineId: "sales",
  name: "Sales Pipeline",
  type: "sales",
  isDefault: true,
  stages: DEFAULT_SALES_PIPELINE_STAGES,
};

const ORDER_PIPELINE_STAGE_COLORS = {
  order_confirmed: "#2563eb",
  advance_received: "#0ea5e9",
  final_measurement: "#06b6d4",
  design_fabric_approval: "#8b5cf6",
  material_ordered_reserved: "#f59e0b",
  material_received: "#10b981",
  production_stitching: "#f97316",
  qc: "#6366f1",
  installation_scheduled: "#14b8a6",
  installation_completed: "#22c55e",
  snag_correction: "#ef4444",
  final_payment_pending: "#d97706",
  project_closed: "#16a34a",
  review_referral_requested: "#7c3aed",
  cancelled: "#64748b",
};

export const DEFAULT_ORDER_PIPELINE_STAGES = [
  { stageId: "order_confirmed", label: "Order Confirmed" },
  { stageId: "advance_received", label: "Advance Received" },
  { stageId: "final_measurement", label: "Final Measurement" },
  { stageId: "design_fabric_approval", label: "Design / Fabric Approval" },
  { stageId: "material_ordered_reserved", label: "Material Ordered / Reserved" },
  { stageId: "material_received", label: "Material Received" },
  { stageId: "production_stitching", label: "Production / Stitching" },
  { stageId: "qc", label: "QC" },
  { stageId: "installation_scheduled", label: "Installation Scheduled" },
  { stageId: "installation_completed", label: "Installation Completed" },
  { stageId: "snag_correction", label: "Snag / Correction" },
  { stageId: "final_payment_pending", label: "Final Payment Pending" },
  { stageId: "project_closed", label: "Project Closed" },
  { stageId: "review_referral_requested", label: "Review / Referral Requested" },
  { stageId: "cancelled", label: "Cancelled" },
].map((stage, index) => ({
  ...stage,
  id: stage.stageId,
  order: (index + 1) * 10,
  active: true,
  color: ORDER_PIPELINE_STAGE_COLORS[stage.stageId] || "#64748b",
  isClosed: stage.stageId === "project_closed",
  isCancelled: stage.stageId === "cancelled",
  systemStage: true,
}));

export const DEFAULT_ORDER_PIPELINE = {
  pipelineId: "orders",
  name: "Orders Pipeline",
  type: "orders",
  isDefault: true,
  stages: DEFAULT_ORDER_PIPELINE_STAGES,
};

export const CRM_TASK_STATUS_OPEN = "open";
export const CRM_TASK_STATUS_COMPLETED = "completed";
export const CRM_TASK_STATUS_CANCELLED = "cancelled";
export const CRM_TASK_TYPES = [
  "call",
  "whatsapp",
  "site_visit",
  "measurement",
  "quotation",
  "follow_up",
  "payment",
  "installation",
  "other",
];
export const CRM_TASK_PRIORITIES = ["low", "normal", "high", "urgent"];
export const TRACKING_ITEM_TYPES = [
  { key: "fabric", label: "Fabric" },
  { key: "lining", label: "Lining" },
  { key: "track", label: "Track" },
  { key: "motor", label: "Motor" },
  { key: "wallpaper", label: "Wallpaper" },
  { key: "sofa_fabric", label: "Sofa fabric" },
  { key: "blind_material", label: "Blind material" },
  { key: "foam", label: "Foam" },
  { key: "wood", label: "Wood" },
  { key: "hardware", label: "Hardware" },
  { key: "other", label: "Other" },
];
export const TRACKING_STATUSES = [
  { key: "not_ordered", label: "Not ordered" },
  { key: "ordered", label: "Ordered" },
  { key: "in_transit", label: "In transit" },
  { key: "reached_local_hub", label: "Reached local hub" },
  { key: "partially_received", label: "Partially received" },
  { key: "received", label: "Received" },
  { key: "delayed", label: "Delayed" },
  { key: "cancelled", label: "Cancelled" },
];
export const MATERIAL_TYPES = TRACKING_ITEM_TYPES;
export const MATERIAL_STATUSES = TRACKING_STATUSES;
export const STANDARD_ORDER_TASK_TEMPLATE_KEY = "standard_furnishing_order";
export const ORDER_TASK_TEMPLATES = {
  [STANDARD_ORDER_TASK_TEMPLATE_KEY]: [
    {
      templateTaskKey: "confirm_measurement",
      taskType: "measurement",
      title: "Confirm final measurements",
      defaultDueOffsetDays: 1,
      ownerMode: "project_owner",
      priority: "high",
    },
    {
      templateTaskKey: "confirm_selection",
      taskType: "other",
      title: "Confirm fabric/design selection",
      defaultDueOffsetDays: 1,
      ownerMode: "project_owner",
      priority: "high",
    },
    {
      templateTaskKey: "reserve_material",
      taskType: "other",
      title: "Order or reserve material",
      defaultDueOffsetDays: 2,
      ownerMode: "project_owner",
      priority: "normal",
    },
    {
      templateTaskKey: "assign_production",
      taskType: "other",
      title: "Assign production / stitching",
      defaultDueOffsetDays: 3,
      ownerMode: "project_owner",
      priority: "normal",
    },
    {
      templateTaskKey: "schedule_installation",
      taskType: "installation",
      title: "Schedule installation",
      defaultDueOffsetDays: 5,
      ownerMode: "project_owner",
      priority: "normal",
    },
    {
      templateTaskKey: "collect_balance",
      taskType: "payment",
      title: "Collect balance payment",
      defaultDueOffsetDays: 7,
      ownerMode: "project_owner",
      priority: "high",
    },
    {
      templateTaskKey: "review_referral",
      taskType: "follow_up",
      title: "Request customer review / referral",
      defaultDueOffsetDays: 10,
      ownerMode: "sales_owner",
      priority: "normal",
    },
  ],
};
export const CRM_LOST_REASONS = [
  "price",
  "competitor",
  "postponed",
  "no_response",
  "no_decision",
  "product_not_available",
  "timeline_issue",
  "outside_service_area",
  "duplicate",
  "invalid",
  "other",
];
export const CRM_ACTIVITY_TYPES = [
  "created",
  "stage_changed",
  "note",
  "call",
  "whatsapp",
  "quote_created",
  "quote_updated",
  "quote_sent",
  "task_created",
  "task_completed",
  "tracking_item_added",
  "tracking_item_updated",
  "tracking_item_ordered",
  "tracking_item_in_transit",
  "tracking_item_local_hub",
  "tracking_item_partially_received",
  "tracking_item_received",
  "tracking_item_delayed",
  "tracking_item_cancelled",
  "tracking_daily_check_completed",
  "tracking_all_received",
  "payment_recorded",
  "payment_follow_up_created",
  "payment_follow_up_completed",
  "payment_completed",
  "material_added",
  "material_updated",
  "material_ordered",
  "material_in_transit",
  "material_partially_received",
  "material_received",
  "material_delayed",
  "material_cancelled",
  "lost",
  "won",
];

export const PROJECT_STAGE_HANDOVER_PENDING = "handover_pending";
export const PROJECT_STAGE_HANDOVER_COMPLETED = "handover_completed";
export const PROJECT_STAGE_IN_PROGRESS = "in_progress";
export const PROJECT_STAGE_COMPLETED = "completed";
export const PROJECT_STAGE_ORDER_HANDOVER = "order_handover";
export const PROJECT_STAGE_FINAL_MEASUREMENT = "final_measurement";
export const PROJECT_STAGE_DESIGN_FABRIC_APPROVAL = "design_fabric_approval";
export const PROJECT_STAGE_MATERIAL_ORDERED_RESERVED = "material_ordered_reserved";
export const PROJECT_STAGE_MATERIAL_RECEIVED = "material_received";
export const PROJECT_STAGE_PRODUCTION_STITCHING = "production_stitching";
export const PROJECT_STAGE_QC = "qc";
export const PROJECT_STAGE_INSTALLATION_SCHEDULED = "installation_scheduled";
export const PROJECT_STAGE_INSTALLATION_COMPLETED = "installation_completed";
export const PROJECT_STAGE_SNAG_CORRECTION = "snag_correction";
export const PROJECT_STAGE_FINAL_PAYMENT_PENDING = "final_payment_pending";
export const PROJECT_STAGE_PROJECT_CLOSED = "project_closed";
export const PROJECT_STAGE_REVIEW_REFERRAL_REQUESTED = "review_referral_requested";
export const PROJECT_STAGE_CANCELLED = "cancelled";
export const PROJECT_STAGES = [
  { key: PROJECT_STAGE_ORDER_HANDOVER, label: "Order Handover" },
  { key: PROJECT_STAGE_FINAL_MEASUREMENT, label: "Final Measurement" },
  { key: PROJECT_STAGE_DESIGN_FABRIC_APPROVAL, label: "Design / Fabric Approval" },
  { key: PROJECT_STAGE_MATERIAL_ORDERED_RESERVED, label: "Material Ordered / Reserved" },
  { key: PROJECT_STAGE_MATERIAL_RECEIVED, label: "Material Received" },
  { key: PROJECT_STAGE_PRODUCTION_STITCHING, label: "Production / Stitching" },
  { key: PROJECT_STAGE_QC, label: "QC" },
  { key: PROJECT_STAGE_INSTALLATION_SCHEDULED, label: "Installation Scheduled" },
  { key: PROJECT_STAGE_INSTALLATION_COMPLETED, label: "Installation Completed" },
  { key: PROJECT_STAGE_SNAG_CORRECTION, label: "Snag / Correction" },
  { key: PROJECT_STAGE_FINAL_PAYMENT_PENDING, label: "Final Payment Pending" },
  { key: PROJECT_STAGE_PROJECT_CLOSED, label: "Project Closed" },
  { key: PROJECT_STAGE_REVIEW_REFERRAL_REQUESTED, label: "Review / Referral Requested" },
  { key: PROJECT_STAGE_HANDOVER_PENDING, label: "Handover pending" },
  { key: PROJECT_STAGE_HANDOVER_COMPLETED, label: "Handover completed" },
  { key: PROJECT_STAGE_IN_PROGRESS, label: "In progress" },
  { key: PROJECT_STAGE_COMPLETED, label: "Completed" },
  { key: PROJECT_STAGE_CANCELLED, label: "Cancelled" },
];

export const PROJECT_ACTIVE_STAGES = [
  PROJECT_STAGE_ORDER_HANDOVER,
  PROJECT_STAGE_FINAL_MEASUREMENT,
  PROJECT_STAGE_DESIGN_FABRIC_APPROVAL,
  PROJECT_STAGE_MATERIAL_ORDERED_RESERVED,
  PROJECT_STAGE_MATERIAL_RECEIVED,
  PROJECT_STAGE_PRODUCTION_STITCHING,
  PROJECT_STAGE_QC,
  PROJECT_STAGE_INSTALLATION_SCHEDULED,
  PROJECT_STAGE_INSTALLATION_COMPLETED,
  PROJECT_STAGE_SNAG_CORRECTION,
  PROJECT_STAGE_FINAL_PAYMENT_PENDING,
  PROJECT_STAGE_REVIEW_REFERRAL_REQUESTED,
  PROJECT_STAGE_HANDOVER_PENDING,
  PROJECT_STAGE_HANDOVER_COMPLETED,
  PROJECT_STAGE_IN_PROGRESS,
];

export const PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED = "not_scheduled";
export const PROJECT_INSTALLATION_STATUS_SCHEDULED = "scheduled";
export const PROJECT_INSTALLATION_STATUS_COMPLETED = "completed";
export const PROJECT_INSTALLATION_STATUS_RESCHEDULED = "rescheduled";
export const PROJECT_INSTALLATION_STATUS_CANCELLED = "cancelled";
export const PROJECT_INSTALLATION_STATUSES = [
  { key: PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED, label: "Not scheduled" },
  { key: PROJECT_INSTALLATION_STATUS_SCHEDULED, label: "Scheduled" },
  { key: PROJECT_INSTALLATION_STATUS_COMPLETED, label: "Completed" },
  { key: PROJECT_INSTALLATION_STATUS_RESCHEDULED, label: "Rescheduled" },
  { key: PROJECT_INSTALLATION_STATUS_CANCELLED, label: "Cancelled" },
];

export const PROJECT_REVIEW_STATUS_NOT_REQUESTED = "not_requested";
export const PROJECT_REVIEW_STATUS_PENDING = "pending";
export const PROJECT_REVIEW_STATUS_COMPLETED = "completed";
export const PROJECT_REVIEW_STATUS_SKIPPED = "skipped";
export const PROJECT_REVIEW_STATUSES = [
  { key: PROJECT_REVIEW_STATUS_NOT_REQUESTED, label: "Not requested" },
  { key: PROJECT_REVIEW_STATUS_PENDING, label: "Review requested" },
  { key: PROJECT_REVIEW_STATUS_COMPLETED, label: "Review received" },
  { key: PROJECT_REVIEW_STATUS_SKIPPED, label: "Skipped" },
];

export const PROJECT_EXECUTION_CHECKLIST_GROUPS = [
  {
    key: "handover",
    label: "Handover",
    items: [
      { key: "quoteReviewed", label: "Quote reviewed" },
      { key: "customerDetailsConfirmed", label: "Customer details confirmed" },
      { key: "siteDetailsConfirmed", label: "Site details confirmed" },
      { key: "advanceReceived", label: "Advance received" },
    ],
  },
  {
    key: "measurement",
    label: "Measurement",
    items: [
      { key: "finalMeasurementDone", label: "Final measurement done" },
      { key: "dimensionsApproved", label: "Dimensions approved" },
    ],
  },
  {
    key: "selection",
    label: "Design / Fabric",
    items: [
      { key: "fabricApproved", label: "Fabric/design approved" },
      { key: "materialReservedOrOrdered", label: "Material reserved / ordered" },
    ],
  },
  {
    key: "production",
    label: "Production",
    items: [
      { key: "materialReceived", label: "Material received" },
      { key: "productionStarted", label: "Production started" },
      { key: "productionCompleted", label: "Production completed" },
      { key: "qcCompleted", label: "QC completed" },
    ],
  },
  {
    key: "installation",
    label: "Installation",
    items: [
      { key: "installationDateFixed", label: "Installation date fixed" },
      { key: "installationCompleted", label: "Installation completed" },
      { key: "customerSignoffReceived", label: "Customer sign-off received" },
    ],
  },
  {
    key: "closure",
    label: "Closure",
    items: [
      { key: "snagsResolved", label: "Snags resolved" },
      { key: "finalPaymentReceived", label: "Final payment received" },
      { key: "projectClosed", label: "Project closed" },
      { key: "reviewRequested", label: "Review/referral requested" },
    ],
  },
];

export const PROJECT_EXECUTION_CHECKLIST_KEYS = PROJECT_EXECUTION_CHECKLIST_GROUPS.flatMap(
  (group) => group.items.map((item) => item.key)
);

export const PROJECT_EXECUTION_CHECKLIST_LABELS = PROJECT_EXECUTION_CHECKLIST_GROUPS.reduce(
  (labels, group) => {
    group.items.forEach((item) => {
      labels[item.key] = item.label;
    });
    return labels;
  },
  {}
);

export const PROJECT_PAYMENT_STATUS_ADVANCE_PENDING = "advance_pending";
export const PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED = "advance_received";
export const PROJECT_PAYMENT_STATUS_PART_PAID = "part_paid";
export const PROJECT_PAYMENT_STATUS_FULLY_PAID = "fully_paid";
export const PROJECT_PAYMENT_STATUS_OVERDUE = "overdue";
export const PROJECT_PAYMENT_STATUS_CANCELLED = "cancelled";
export const PROJECT_PAYMENT_STATUS = [
  { key: PROJECT_PAYMENT_STATUS_ADVANCE_PENDING, label: "Advance pending" },
  { key: PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED, label: "Advance received" },
  { key: PROJECT_PAYMENT_STATUS_PART_PAID, label: "Part paid" },
  { key: PROJECT_PAYMENT_STATUS_FULLY_PAID, label: "Fully paid" },
  { key: PROJECT_PAYMENT_STATUS_OVERDUE, label: "Payment overdue" },
  { key: PROJECT_PAYMENT_STATUS_CANCELLED, label: "Cancelled" },
];
export const PAYMENT_STATUSES = PROJECT_PAYMENT_STATUS;

export const PAYMENT_TYPES = [
  { key: "advance", label: "Advance" },
  { key: "part_payment", label: "Part payment" },
  { key: "balance", label: "Balance" },
  { key: "final_payment", label: "Final payment" },
  { key: "refund", label: "Refund" },
  { key: "adjustment", label: "Adjustment" },
];

export const PAYMENT_MODES = [
  { key: "cash", label: "Cash" },
  { key: "upi", label: "UPI" },
  { key: "bank_transfer", label: "Bank transfer" },
  { key: "cheque", label: "Cheque" },
  { key: "card", label: "Card" },
  { key: "other", label: "Other" },
];

export const PROJECT_HANDOVER_CHECKLIST_KEYS = [
  "quotationConfirmed",
  "finalMeasurementConfirmed",
  "customerDetailsConfirmed",
  "siteDetailsConfirmed",
  "fabricSelectionConfirmed",
  "advanceReceived",
  "deliveryTimelineConfirmed",
  "specialNotesAdded",
];

export const PROJECT_HANDOVER_CHECKLIST_LABELS = {
  quotationConfirmed: "Final quotation confirmed",
  finalMeasurementConfirmed: "Measurements confirmed",
  customerDetailsConfirmed: "Customer details confirmed",
  siteDetailsConfirmed: "Site details confirmed",
  fabricSelectionConfirmed: "Fabric/design selection confirmed",
  advanceReceived: "Advance received",
  deliveryTimelineConfirmed: "Delivery timeline confirmed",
  specialNotesAdded: "Special notes added",
};

export const DEFAULT_CRM_SETTINGS = {
  enableCrm: true,
  warnWhenNoNextAction: true,
  requireNextAction: false,
  defaultQuoteFollowUpDays: 3,
  firstContactSlaMinutes: 10,
  enableQuoteSentFollowUpPrompt: true,
  autoCreateDealFromQuotation: true,
  enableAttentionRequired: true,
  enablePipelineReview: true,
  enableOrderHandover: true,
  enableOrderTaskTemplates: true,
  enableDeliveryTracking: true,
  enableDailyTrackingReminder: true,
  defaultTrackingReminderTime: "10:30",
  defaultTrackingOwnerMode: "project_owner",
  enableMaterialTracking: true,
  enablePaymentTracking: true,
  autoCreatePaymentFollowUp: true,
  defaultPaymentFollowUpDays: 7,
  defaultPaymentOwnerMode: "project_owner",
  autoCreateInstallationTask: true,
  autoCreateReviewTaskAfterClose: true,
  defaultReviewRequestDaysAfterClose: 1,
  requireInstallationBeforeClose: true,
  requireSnagResolutionBeforeClose: true,
  requireFinalPaymentBeforeClose: true,
  defaultOrderTaskTemplate: STANDARD_ORDER_TASK_TEMPLATE_KEY,
  autoCreateOrderTasks: true,
  requireHandoverChecklistForWon: false,
  defaultTaskOwnerMode: "current_user",
  defaultProjectOwnerMode: "current_user",
  defaultHandoverDueDays: 1,
};

export const normalizeCrmSettings = (settings = {}) => {
  const defaultQuoteFollowUpDays = Math.max(
    0,
    Math.round(
      Number(
        settings.defaultQuoteFollowUpDays ??
          DEFAULT_CRM_SETTINGS.defaultQuoteFollowUpDays
      ) || DEFAULT_CRM_SETTINGS.defaultQuoteFollowUpDays
    )
  );
  const firstContactSlaMinutes = Math.max(
    0,
    Math.round(
      Number(
        settings.firstContactSlaMinutes ??
          DEFAULT_CRM_SETTINGS.firstContactSlaMinutes
      ) || DEFAULT_CRM_SETTINGS.firstContactSlaMinutes
    )
  );
  const defaultTaskOwnerMode = String(
    settings.defaultTaskOwnerMode || DEFAULT_CRM_SETTINGS.defaultTaskOwnerMode
  ).trim();
  const defaultProjectOwnerMode = String(
    settings.defaultProjectOwnerMode ||
      DEFAULT_CRM_SETTINGS.defaultProjectOwnerMode
  ).trim();
  const defaultOrderTaskTemplate = String(
    settings.defaultOrderTaskTemplate ||
      DEFAULT_CRM_SETTINGS.defaultOrderTaskTemplate
  ).trim();
  const defaultHandoverDueDays = Math.max(
    0,
    Math.round(
      Number(
        settings.defaultHandoverDueDays ??
          DEFAULT_CRM_SETTINGS.defaultHandoverDueDays
      ) || DEFAULT_CRM_SETTINGS.defaultHandoverDueDays
    )
  );
  const defaultTrackingReminderTime = String(
    settings.defaultTrackingReminderTime ||
      DEFAULT_CRM_SETTINGS.defaultTrackingReminderTime
  )
    .trim()
    .slice(0, 5);
  const defaultTrackingOwnerMode = String(
    settings.defaultTrackingOwnerMode ||
      DEFAULT_CRM_SETTINGS.defaultTrackingOwnerMode
  ).trim();
  const defaultPaymentFollowUpDays = Math.max(
    0,
    Math.round(
      Number(
        settings.defaultPaymentFollowUpDays ??
          DEFAULT_CRM_SETTINGS.defaultPaymentFollowUpDays
      ) || DEFAULT_CRM_SETTINGS.defaultPaymentFollowUpDays
    )
  );
  const defaultPaymentOwnerMode = String(
    settings.defaultPaymentOwnerMode ||
      DEFAULT_CRM_SETTINGS.defaultPaymentOwnerMode
  ).trim();
  const defaultReviewRequestDaysAfterClose = Math.max(
    0,
    Math.round(
      Number(
        settings.defaultReviewRequestDaysAfterClose ??
          DEFAULT_CRM_SETTINGS.defaultReviewRequestDaysAfterClose
      ) || DEFAULT_CRM_SETTINGS.defaultReviewRequestDaysAfterClose
    )
  );

  return {
    enableCrm: settings.enableCrm !== false,
    warnWhenNoNextAction: settings.warnWhenNoNextAction !== false,
    requireNextAction: Boolean(settings.requireNextAction),
    defaultQuoteFollowUpDays,
    firstContactSlaMinutes,
    enableQuoteSentFollowUpPrompt: settings.enableQuoteSentFollowUpPrompt !== false,
    autoCreateDealFromQuotation: settings.autoCreateDealFromQuotation !== false,
    enableAttentionRequired: settings.enableAttentionRequired !== false,
    enablePipelineReview: settings.enablePipelineReview !== false,
    enableOrderHandover: settings.enableOrderHandover !== false,
    enableOrderTaskTemplates: settings.enableOrderTaskTemplates !== false,
    enableDeliveryTracking:
      settings.enableDeliveryTracking !== false &&
      settings.enableMaterialTracking !== false,
    enableDailyTrackingReminder: settings.enableDailyTrackingReminder !== false,
    defaultTrackingReminderTime: /^\d{2}:\d{2}$/.test(defaultTrackingReminderTime)
      ? defaultTrackingReminderTime
      : DEFAULT_CRM_SETTINGS.defaultTrackingReminderTime,
    defaultTrackingOwnerMode: [
      "project_owner",
      "current_user",
      "workspace_owner",
    ].includes(defaultTrackingOwnerMode)
      ? defaultTrackingOwnerMode
      : DEFAULT_CRM_SETTINGS.defaultTrackingOwnerMode,
    enableMaterialTracking: settings.enableMaterialTracking !== false,
    enablePaymentTracking: settings.enablePaymentTracking !== false,
    autoCreatePaymentFollowUp: settings.autoCreatePaymentFollowUp !== false,
    defaultPaymentFollowUpDays,
    defaultPaymentOwnerMode: [
      "project_owner",
      "current_user",
      "workspace_owner",
    ].includes(defaultPaymentOwnerMode)
      ? defaultPaymentOwnerMode
      : DEFAULT_CRM_SETTINGS.defaultPaymentOwnerMode,
    autoCreateInstallationTask: settings.autoCreateInstallationTask !== false,
    autoCreateReviewTaskAfterClose:
      settings.autoCreateReviewTaskAfterClose !== false,
    defaultReviewRequestDaysAfterClose,
    requireInstallationBeforeClose:
      settings.requireInstallationBeforeClose !== false,
    requireSnagResolutionBeforeClose:
      settings.requireSnagResolutionBeforeClose !== false,
    requireFinalPaymentBeforeClose:
      settings.requireFinalPaymentBeforeClose !== false,
    defaultOrderTaskTemplate: Object.prototype.hasOwnProperty.call(
      ORDER_TASK_TEMPLATES,
      defaultOrderTaskTemplate
    )
      ? defaultOrderTaskTemplate
      : DEFAULT_CRM_SETTINGS.defaultOrderTaskTemplate,
    autoCreateOrderTasks: settings.autoCreateOrderTasks !== false,
    requireHandoverChecklistForWon: Boolean(
      settings.requireHandoverChecklistForWon
    ),
    defaultTaskOwnerMode: [
      "current_user",
      "lead_owner",
      "workspace_owner",
    ].includes(defaultTaskOwnerMode)
      ? defaultTaskOwnerMode
      : DEFAULT_CRM_SETTINGS.defaultTaskOwnerMode,
    defaultProjectOwnerMode: [
      "current_user",
      "lead_owner",
      "workspace_owner",
    ].includes(defaultProjectOwnerMode)
      ? defaultProjectOwnerMode
      : DEFAULT_CRM_SETTINGS.defaultProjectOwnerMode,
    defaultHandoverDueDays,
  };
};

const normalizeCrmKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const normalizeCrmDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return normalizeCrmDate(value.toDate());
  const date =
    value instanceof Date
      ? new Date(value.getTime())
      : typeof value === "number"
      ? new Date(value)
      : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatCrmDate = (value) => {
  const date = normalizeCrmDate(value);
  if (!date) return "No date set";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getOptionLabel = (options = [], key = "", fallback = "") =>
  options.find((option) => option.key === key)?.label || fallback || key;

export const normalizeTrackingItemType = (type = "") => {
  const normalized = normalizeCrmKey(type || "other");
  return TRACKING_ITEM_TYPES.some((option) => option.key === normalized)
    ? normalized
    : "other";
};

export const getTrackingItemTypeLabel = (type = "") =>
  getOptionLabel(TRACKING_ITEM_TYPES, normalizeTrackingItemType(type), "Other");

export const normalizeTrackingStatus = (status = "") => {
  const normalized = normalizeCrmKey(status || "not_ordered");
  return TRACKING_STATUSES.some((option) => option.key === normalized)
    ? normalized
    : "not_ordered";
};

export const getTrackingStatusLabel = (status = "") =>
  getOptionLabel(TRACKING_STATUSES, normalizeTrackingStatus(status), "Not ordered");

export const normalizeMaterialType = normalizeTrackingItemType;
export const getMaterialTypeLabel = getTrackingItemTypeLabel;
export const normalizeMaterialStatus = normalizeTrackingStatus;
export const getMaterialStatusLabel = getTrackingStatusLabel;

const toSafeMaterialNumber = (value) => {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Number(number.toFixed(2))) : null;
};

export const calculateTrackingPendingQty = (quantity, receivedQuantity) => {
  const total = toSafeMaterialNumber(quantity);
  if (total == null) return null;
  const received = toSafeMaterialNumber(receivedQuantity) || 0;
  return Math.max(0, Number((total - received).toFixed(2)));
};

export const calculateMaterialPendingQty = calculateTrackingPendingQty;

export const isTrackingItemOpen = (item = {}) => {
  const status = normalizeTrackingStatus(item.status);
  return !["received", "cancelled"].includes(status);
};

export const isTrackingItemDelayed = (item = {}, now = new Date()) => {
  const status = normalizeTrackingStatus(item.status);
  if (status === "delayed") return true;
  if (["received", "cancelled"].includes(status)) return false;
  const expectedDate = normalizeCrmDate(item.expectedArrivalDate);
  const nowDate = normalizeCrmDate(now);
  if (!expectedDate || !nowDate) return false;
  const expectedStart = new Date(
    expectedDate.getFullYear(),
    expectedDate.getMonth(),
    expectedDate.getDate()
  ).getTime();
  const todayStart = new Date(
    nowDate.getFullYear(),
    nowDate.getMonth(),
    nowDate.getDate()
  ).getTime();
  return expectedStart < todayStart;
};

export const isTrackingItemOverdue = isTrackingItemDelayed;
export const isMaterialDelayed = isTrackingItemDelayed;

export const buildTrackingItemDisplayTitle = (item = {}) => {
  const parts = [
    item.itemName,
    item.brandName,
    item.catalogueName,
    item.srNumber ? `SR ${item.srNumber}` : "",
    item.fabricCode,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  return parts.join(" | ") || getTrackingItemTypeLabel(item.itemType || item.materialType);
};

export const buildMaterialDisplayTitle = buildTrackingItemDisplayTitle;

export const formatTrackingInfo = (item = {}) => {
  const trackingNumber = String(item.trackingNumber || "").trim();
  const lrNumber = String(item.lrNumber || "").trim();
  const parts = [
    item.courierName || item.transportName,
    trackingNumber,
    lrNumber && lrNumber !== trackingNumber ? `LR ${lrNumber}` : "",
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  return parts.join(" | ") || "Not set";
};

export const buildProjectTrackingSummary = (items = [], options = {}) => {
  const list = Array.isArray(items) ? items : [];
  const counts = list.reduce(
    (summary, item) => {
      const status = normalizeTrackingStatus(item?.status);
      summary.totalItemCount += 1;
      if (status === "received") summary.receivedItemCount += 1;
      if (status === "delayed" || isTrackingItemDelayed(item, options.now)) {
        summary.delayedItemCount += 1;
      }
      if (isTrackingItemOpen({ ...item, status })) summary.openItemCount += 1;
      return summary;
    },
    {
      openItemCount: 0,
      delayedItemCount: 0,
      receivedItemCount: 0,
      totalItemCount: 0,
    }
  );
  return {
    enabled: counts.openItemCount > 0,
    trackingComplete: counts.totalItemCount > 0 && counts.openItemCount === 0,
    ...counts,
  };
};

const sanitizeTrackingSegment = (value = "") =>
  String(value || "item")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "item";

export const calculateNextTrackingCheckTime = ({
  dailyCheckTime = DEFAULT_CRM_SETTINGS.defaultTrackingReminderTime,
  timezone = "Asia/Kolkata",
  fromDate = new Date(),
} = {}) => {
  const base = normalizeCrmDate(fromDate) || new Date();
  const [hoursText, minutesText] = String(dailyCheckTime || "10:30").split(":");
  const hours = Math.min(23, Math.max(0, Number(hoursText) || 10));
  const minutes = Math.min(59, Math.max(0, Number(minutesText) || 30));
  const next = new Date(base.getTime());
  next.setHours(hours, minutes, 0, 0);
  if (next.getTime() <= base.getTime()) {
    next.setDate(next.getDate() + 1);
  }
  return {
    date: next,
    dateKey: next.toISOString().slice(0, 10),
    dailyCheckTime: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    timezone: String(timezone || "Asia/Kolkata").trim() || "Asia/Kolkata",
  };
};

export const buildDailyTrackingTaskId = ({ projectId = "", date } = {}) => {
  const normalizedProjectId = sanitizeTrackingSegment(projectId || "project");
  const rawDate =
    date instanceof Date
      ? date
      : normalizeCrmDate(date) || new Date();
  const dateKey = rawDate.toISOString().slice(0, 10).replace(/-/g, "");
  return `project_${normalizedProjectId}__delivery_tracking__${dateKey}`;
};

const toSafeProjectText = (value = "") => String(value ?? "").trim();

const toSafeProjectNumber = (value = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, number) : 0;
};

export const createDefaultProjectHandoverChecklist = (input = {}) =>
  PROJECT_HANDOVER_CHECKLIST_KEYS.reduce((accumulator, key) => {
    accumulator[key] = Boolean(input?.[key]);
    return accumulator;
  }, {});

export const createDefaultProjectExecutionChecklist = (input = {}) =>
  PROJECT_EXECUTION_CHECKLIST_KEYS.reduce((accumulator, key) => {
    accumulator[key] = Boolean(input?.[key]);
    return accumulator;
  }, {});

export const calculateProjectChecklistSummary = (checklist = {}) => {
  const normalized = createDefaultProjectExecutionChecklist(checklist);
  const totalItems = PROJECT_EXECUTION_CHECKLIST_KEYS.length;
  const completedItems = PROJECT_EXECUTION_CHECKLIST_KEYS.reduce(
    (count, key) => count + (normalized[key] === true ? 1 : 0),
    0
  );
  return {
    totalItems,
    completedItems,
    pendingItems: Math.max(0, totalItems - completedItems),
    completionPercent: totalItems
      ? Math.round((completedItems / totalItems) * 100)
      : 0,
  };
};

export const normalizeProjectInstallationStatus = (status) => {
  const key = normalizeCrmKey(status);
  const legacyMap = {
    not_set: PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED,
    pending: PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED,
    planned: PROJECT_INSTALLATION_STATUS_SCHEDULED,
    scheduled: PROJECT_INSTALLATION_STATUS_SCHEDULED,
    installed: PROJECT_INSTALLATION_STATUS_COMPLETED,
    done: PROJECT_INSTALLATION_STATUS_COMPLETED,
    complete: PROJECT_INSTALLATION_STATUS_COMPLETED,
    completed: PROJECT_INSTALLATION_STATUS_COMPLETED,
    rescheduled: PROJECT_INSTALLATION_STATUS_RESCHEDULED,
    cancelled: PROJECT_INSTALLATION_STATUS_CANCELLED,
    canceled: PROJECT_INSTALLATION_STATUS_CANCELLED,
  };
  const mappedKey = legacyMap[key] || key;
  return PROJECT_INSTALLATION_STATUSES.some((item) => item.key === mappedKey)
    ? mappedKey
    : PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED;
};

export const getProjectInstallationStatusLabel = (status) =>
  PROJECT_INSTALLATION_STATUSES.find(
    (item) => item.key === normalizeProjectInstallationStatus(status)
  )?.label || "Not scheduled";

export const normalizeProjectReviewStatus = (status) => {
  const key = normalizeCrmKey(status);
  const legacyMap = {
    requested: PROJECT_REVIEW_STATUS_PENDING,
    review_requested: PROJECT_REVIEW_STATUS_PENDING,
    received: PROJECT_REVIEW_STATUS_COMPLETED,
    done: PROJECT_REVIEW_STATUS_COMPLETED,
    completed: PROJECT_REVIEW_STATUS_COMPLETED,
    cancelled: PROJECT_REVIEW_STATUS_SKIPPED,
    canceled: PROJECT_REVIEW_STATUS_SKIPPED,
  };
  const mappedKey = legacyMap[key] || key;
  return PROJECT_REVIEW_STATUSES.some((item) => item.key === mappedKey)
    ? mappedKey
    : PROJECT_REVIEW_STATUS_NOT_REQUESTED;
};

export const getProjectReviewStatusLabel = (status) =>
  PROJECT_REVIEW_STATUSES.find(
    (item) => item.key === normalizeProjectReviewStatus(status)
  )?.label || "Not requested";

export const normalizeProjectSnagSummary = (summary = {}) => {
  const source = summary && typeof summary === "object" ? summary : {};
  const openCount = Math.max(0, Math.round(Number(source.openCount || 0) || 0));
  const resolvedCount = Math.max(
    0,
    Math.round(Number(source.resolvedCount || 0) || 0)
  );
  const urgentCount = Math.max(0, Math.round(Number(source.urgentCount || 0) || 0));
  return {
    openCount,
    resolvedCount,
    urgentCount,
    totalCount: Math.max(
      openCount + resolvedCount,
      Math.round(Number(source.totalCount || 0) || 0)
    ),
    nextDueAt: source.nextDueAt || null,
    lastUpdatedAt: source.lastUpdatedAt || null,
  };
};

export const normalizeProjectReviewSummary = (summary = {}) => {
  const source = summary && typeof summary === "object" ? summary : {};
  return {
    status: normalizeProjectReviewStatus(source.status),
    requestedAt: source.requestedAt || null,
    requestedBy: toSafeProjectText(source.requestedBy),
    taskId: toSafeProjectText(source.taskId),
    completedAt: source.completedAt || null,
    completedBy: toSafeProjectText(source.completedBy),
    skippedAt: source.skippedAt || null,
    skippedBy: toSafeProjectText(source.skippedBy),
  };
};

export const calculateBalanceAmount = (orderValue, advanceAmount) => {
  const balance =
    toSafeProjectNumber(orderValue) - toSafeProjectNumber(advanceAmount);
  return Number.isFinite(balance) ? Math.max(0, Number(balance.toFixed(2))) : 0;
};

export const normalizePaymentType = (type = "") => {
  const normalized = normalizeCrmKey(type || "part_payment");
  return PAYMENT_TYPES.some((option) => option.key === normalized)
    ? normalized
    : "part_payment";
};

export const getPaymentTypeLabel = (type = "") =>
  getOptionLabel(PAYMENT_TYPES, normalizePaymentType(type), "Part payment");

export const normalizePaymentMode = (mode = "") => {
  const normalized = normalizeCrmKey(mode || "cash");
  return PAYMENT_MODES.some((option) => option.key === normalized)
    ? normalized
    : "cash";
};

export const getPaymentModeLabel = (mode = "") =>
  getOptionLabel(PAYMENT_MODES, normalizePaymentMode(mode), "Cash");

export const formatMoneySafe = (value = 0) => {
  const amount = toSafeProjectNumber(value);
  return amount.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  });
};

export const getPaymentStatusFromAmounts = ({
  orderValue = 0,
  totalReceived = 0,
  advanceAmount = 0,
  cancelled = false,
} = {}) => {
  if (cancelled) return PROJECT_PAYMENT_STATUS_CANCELLED;
  const value = toSafeProjectNumber(orderValue);
  const received = toSafeProjectNumber(totalReceived);
  if (value > 0 && received >= value) return PROJECT_PAYMENT_STATUS_FULLY_PAID;
  if (received > 0) {
    return received <= toSafeProjectNumber(advanceAmount || received)
      ? PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED
      : PROJECT_PAYMENT_STATUS_PART_PAID;
  }
  return PROJECT_PAYMENT_STATUS_ADVANCE_PENDING;
};

export const isPaymentOverdue = (project = {}, now = new Date()) => {
  const summary = project.paymentSummary || {};
  const balance = toSafeProjectNumber(
    summary.balanceAmount ?? project.balanceAmount ?? 0
  );
  const status = normalizePaymentStatus(summary.paymentStatus || project.paymentStatus);
  if (balance <= 0 || status === PROJECT_PAYMENT_STATUS_FULLY_PAID) return false;
  if (status === PROJECT_PAYMENT_STATUS_CANCELLED) return false;
  const dueDate = normalizeCrmDate(
    summary.nextPaymentDueDate || project.nextPaymentDueDate || null
  );
  const nowDate = normalizeCrmDate(now) || new Date();
  if (!dueDate) return false;
  const dueStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()).getTime();
  const todayStart = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate()).getTime();
  return dueStart < todayStart;
};

export const calculateProjectPaymentSummary = ({ project = {}, payments = [] } = {}) => {
  const existingSummary =
    project.paymentSummary && typeof project.paymentSummary === "object"
      ? project.paymentSummary
      : {};
  const orderValue = toSafeProjectNumber(
    existingSummary.orderValue ?? project.orderValue ?? 0
  );
  const paymentList = Array.isArray(payments) ? payments : [];
  const totalReceivedFromPayments = paymentList.reduce((total, payment) => {
    const amount = toSafeProjectNumber(payment?.amount);
    const type = normalizePaymentType(payment?.paymentType);
    if (type === "refund") return total - amount;
    return total + amount;
  }, 0);
  const legacyAdvance = toSafeProjectNumber(
    existingSummary.advanceAmount ?? project.advanceAmount ?? 0
  );
  const legacyReceived = toSafeProjectNumber(
    existingSummary.totalReceived ?? project.totalReceived ?? legacyAdvance
  );
  const advancePayment = paymentList.find(
    (payment) => normalizePaymentType(payment?.paymentType) === "advance"
  );
  const legacyAdvanceCarryForward =
    paymentList.length > 0 && !advancePayment && legacyAdvance > 0 ? legacyAdvance : 0;
  const totalReceived =
    paymentList.length > 0
      ? Math.max(
          0,
          Number((totalReceivedFromPayments + legacyAdvanceCarryForward).toFixed(2))
        )
      : legacyReceived;
  const advanceAmount = paymentList.length > 0
    ? toSafeProjectNumber(advancePayment?.amount || legacyAdvanceCarryForward || 0)
    : legacyAdvance;
  const balanceAmount = calculateBalanceAmount(orderValue, totalReceived);
  const lastPayment = paymentList
    .slice()
    .sort(
      (left, right) =>
        (normalizeCrmDate(right?.paymentDate)?.getTime() || 0) -
        (normalizeCrmDate(left?.paymentDate)?.getTime() || 0)
    )[0];
  const status = getPaymentStatusFromAmounts({
    orderValue,
    totalReceived,
    advanceAmount,
    cancelled:
      normalizePaymentStatus(project.paymentStatus) === PROJECT_PAYMENT_STATUS_CANCELLED,
  });
  const existingNextDueAmount = toSafeProjectNumber(existingSummary.nextPaymentDueAmount);
  const summary = {
    ...existingSummary,
    orderValue,
    totalReceived,
    advanceAmount,
    balanceAmount,
    paymentStatus: status,
    nextPaymentDueAmount:
      balanceAmount > 0
        ? existingNextDueAmount > 0
          ? Math.min(existingNextDueAmount, balanceAmount)
          : balanceAmount
        : 0,
    nextPaymentDueDate:
      balanceAmount > 0
        ? existingSummary.nextPaymentDueDate || project.expectedInstallationDate || null
        : null,
    paymentFollowUpOwnerUid:
      existingSummary.paymentFollowUpOwnerUid || project.projectOwnerUid || "",
    paymentFollowUpOwnerName:
      existingSummary.paymentFollowUpOwnerName || project.projectOwnerName || "",
    lastPaymentAt: lastPayment?.paymentDate || existingSummary.lastPaymentAt || null,
    lastPaymentAmount: lastPayment
      ? toSafeProjectNumber(lastPayment.amount)
      : toSafeProjectNumber(existingSummary.lastPaymentAmount || 0),
    paymentCompletedAt:
      status === PROJECT_PAYMENT_STATUS_FULLY_PAID
        ? existingSummary.paymentCompletedAt || new Date()
        : null,
  };
  return {
    ...summary,
    paymentOverdue: isPaymentOverdue({ ...project, paymentSummary: summary }),
  };
};

export const normalizeProjectStage = (stage) => {
  const key = normalizeCrmKey(stage);
  const legacyMap = {
    won: PROJECT_STAGE_ORDER_HANDOVER,
    order_confirmed: PROJECT_STAGE_ORDER_HANDOVER,
    handover: PROJECT_STAGE_ORDER_HANDOVER,
    handover_done: PROJECT_STAGE_FINAL_MEASUREMENT,
    active: PROJECT_STAGE_PRODUCTION_STITCHING,
    installed: PROJECT_STAGE_INSTALLATION_COMPLETED,
    done: PROJECT_STAGE_PROJECT_CLOSED,
    closed: PROJECT_STAGE_PROJECT_CLOSED,
    canceled: PROJECT_STAGE_CANCELLED,
  };
  const mappedKey = legacyMap[key] || key;
  return PROJECT_STAGES.some((stageOption) => stageOption.key === mappedKey)
    ? mappedKey
    : PROJECT_STAGE_ORDER_HANDOVER;
};

export const getProjectStageLabel = (stage) =>
  PROJECT_STAGES.find((item) => item.key === normalizeProjectStage(stage))
    ?.label || "Order Handover";

export const mapLegacyProjectStageToOrderPipelineStage = (stage) => {
  const key = normalizeCrmKey(stage);
  const legacyMap = {
    order_handover: "order_confirmed",
    handover_pending: "order_confirmed",
    order_confirmed: "order_confirmed",
    won: "order_confirmed",
    handover: "order_confirmed",
    legacy_handover_pending: "order_confirmed",
    handover_completed: "advance_received",
    legacy_handover_completed: "advance_received",
    advance_received: "advance_received",
    final_measurement: "final_measurement",
    design_fabric_approval: "design_fabric_approval",
    material_ordered: "material_ordered_reserved",
    material_reserved: "material_ordered_reserved",
    material_ordered_reserved: "material_ordered_reserved",
    material_received: "material_received",
    production: "production_stitching",
    stitching: "production_stitching",
    active: "production_stitching",
    production_stitching: "production_stitching",
    qc: "qc",
    installation_scheduled: "installation_scheduled",
    installed: "installation_completed",
    installation_completed: "installation_completed",
    snag: "snag_correction",
    snag_correction: "snag_correction",
    final_payment_pending: "final_payment_pending",
    closed: "project_closed",
    completed: "project_closed",
    done: "project_closed",
    project_closed: "project_closed",
    legacy_completed: "project_closed",
    review_pending: "review_referral_requested",
    review_referral_requested: "review_referral_requested",
    cancelled: "cancelled",
    canceled: "cancelled",
  };
  const mapped = legacyMap[key] || key;
  return DEFAULT_ORDER_PIPELINE_STAGES.some((stageOption) => stageOption.stageId === mapped)
    ? mapped
    : "order_confirmed";
};

export const mapOrderPipelineStageToLegacyProjectStage = (stageId) => {
  const key = mapLegacyProjectStageToOrderPipelineStage(stageId);
  const legacyMap = {
    order_confirmed: PROJECT_STAGE_ORDER_HANDOVER,
    advance_received: PROJECT_STAGE_HANDOVER_COMPLETED,
    final_measurement: PROJECT_STAGE_FINAL_MEASUREMENT,
    design_fabric_approval: PROJECT_STAGE_DESIGN_FABRIC_APPROVAL,
    material_ordered_reserved: PROJECT_STAGE_MATERIAL_ORDERED_RESERVED,
    material_received: PROJECT_STAGE_MATERIAL_RECEIVED,
    production_stitching: PROJECT_STAGE_PRODUCTION_STITCHING,
    qc: PROJECT_STAGE_QC,
    installation_scheduled: PROJECT_STAGE_INSTALLATION_SCHEDULED,
    installation_completed: PROJECT_STAGE_INSTALLATION_COMPLETED,
    snag_correction: PROJECT_STAGE_SNAG_CORRECTION,
    final_payment_pending: PROJECT_STAGE_FINAL_PAYMENT_PENDING,
    project_closed: PROJECT_STAGE_PROJECT_CLOSED,
    review_referral_requested: PROJECT_STAGE_REVIEW_REFERRAL_REQUESTED,
    cancelled: PROJECT_STAGE_CANCELLED,
  };
  return legacyMap[key] || PROJECT_STAGE_ORDER_HANDOVER;
};

export const resolveProjectPipelineStage = (project = {}) =>
  mapLegacyProjectStageToOrderPipelineStage(
    project.projectStage || project.orderStage || project.stage || project.status
  );

export const getOrderPipelineStageLabel = (pipeline = {}, stageId = "") => {
  const resolvedStageId = mapLegacyProjectStageToOrderPipelineStage(stageId);
  return getOrderPipelineStage(pipeline, resolvedStageId)?.label || "Order Confirmed";
};

export const normalizePaymentStatus = (status) => {
  const key = normalizeCrmKey(status);
  const legacyMap = {
    pending: PROJECT_PAYMENT_STATUS_ADVANCE_PENDING,
    unpaid: PROJECT_PAYMENT_STATUS_ADVANCE_PENDING,
    advance: PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED,
    paid_part: PROJECT_PAYMENT_STATUS_PART_PAID,
    partial: PROJECT_PAYMENT_STATUS_PART_PAID,
    paid: PROJECT_PAYMENT_STATUS_FULLY_PAID,
    full_paid: PROJECT_PAYMENT_STATUS_FULLY_PAID,
    full: PROJECT_PAYMENT_STATUS_FULLY_PAID,
    overdue: PROJECT_PAYMENT_STATUS_OVERDUE,
    cancelled: PROJECT_PAYMENT_STATUS_CANCELLED,
    canceled: PROJECT_PAYMENT_STATUS_CANCELLED,
  };
  const mappedKey = legacyMap[key] || key;
  return PROJECT_PAYMENT_STATUS.some((item) => item.key === mappedKey)
    ? mappedKey
    : PROJECT_PAYMENT_STATUS_ADVANCE_PENDING;
};

export const getPaymentStatusLabel = (status) =>
  PROJECT_PAYMENT_STATUS.find((item) => item.key === normalizePaymentStatus(status))
    ?.label || "Advance pending";

export const buildProjectTitle = (project = {}) => {
  const quoteNo = toSafeProjectText(project.quoteNo);
  const customerName = toSafeProjectText(project.customerName);
  const siteName = toSafeProjectText(project.siteName || project.projectName);
  const title = toSafeProjectText(project.projectTitle || project.title);
  if (title) return title;
  return [customerName, siteName, quoteNo ? `#${quoteNo}` : ""]
    .filter(Boolean)
    .join(" - ") || "Untitled order";
};

export const validateProjectHandoverInput = (input = {}) => {
  const errors = [];
  const warnings = [];
  if (!toSafeProjectText(input.customerName)) errors.push("Customer name is required.");
  if (toSafeProjectNumber(input.orderValue) <= 0) {
    errors.push("Order value is required.");
  }
  const projectOwnerUid = toSafeProjectText(input.projectOwnerUid);
  if (!projectOwnerUid || projectOwnerUid === "unassigned") {
    errors.push("Project owner is required.");
  }
  if (!toSafeProjectText(input.quoteId || input.quoteNo) && !toSafeProjectText(input.opportunityId)) {
    errors.push("Link a quotation or opportunity before creating an order.");
  }
  if (!input.expectedInstallationDate) {
    warnings.push("No installation date set.");
  }
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

export const buildProjectFromOpportunityAndQuote = ({
  opportunity = {},
  quoteIndex = {},
  quote = {},
  input = {},
} = {}) => {
  const sourceQuote = quoteIndex || quote || {};
  const sourceOpportunity = opportunity || {};
  const quoteId = toSafeProjectText(
    input.quoteId ||
      sourceQuote.quoteId ||
      sourceQuote.sourceQuoteId ||
      sourceQuote.id ||
      sourceOpportunity.latestQuoteId
  );
  const quoteNo = toSafeProjectText(
    input.quoteNo || sourceQuote.quoteNo || sourceOpportunity.latestQuoteNo || quoteId
  );
  const orderValue = toSafeProjectNumber(
    input.orderValue ||
      sourceQuote.orderValue ||
      sourceQuote.totalAmount ||
      sourceQuote.grandTotal ||
      sourceQuote.finalTotal ||
      sourceOpportunity.orderValue ||
      sourceOpportunity.latestQuoteAmount ||
      sourceOpportunity.estimatedValue
  );
  const advanceAmount = toSafeProjectNumber(input.advanceAmount);
  const balanceAmount = calculateBalanceAmount(orderValue, advanceAmount);
  const checklist = createDefaultProjectHandoverChecklist(input.handoverChecklist);
  const isChecklistComplete = PROJECT_HANDOVER_CHECKLIST_KEYS.every(
    (key) => checklist[key] === true
  );
  const executionChecklist = createDefaultProjectExecutionChecklist({
    quoteReviewed: Boolean(input.quoteId || quoteId || quoteNo),
    customerDetailsConfirmed: Boolean(
      input.customerName ||
        sourceQuote.customerName ||
        sourceOpportunity.customerName ||
        sourceQuote.customerDisplayName
    ),
    siteDetailsConfirmed: Boolean(
      input.siteName ||
        sourceQuote.siteName ||
        sourceQuote.projectName ||
        sourceOpportunity.siteName ||
        sourceOpportunity.siteAddress ||
        sourceOpportunity.contact?.address ||
        sourceOpportunity.address
    ),
    advanceReceived: advanceAmount > 0,
    installationDateFixed: Boolean(input.expectedInstallationDate),
    ...(input.checklist || {}),
  });
  const checklistSummary = calculateProjectChecklistSummary(executionChecklist);
  const fallbackPaymentStatus =
    balanceAmount <= 0 && orderValue > 0
      ? PROJECT_PAYMENT_STATUS_FULLY_PAID
      : advanceAmount > 0
      ? PROJECT_PAYMENT_STATUS_ADVANCE_RECEIVED
      : PROJECT_PAYMENT_STATUS_ADVANCE_PENDING;
  const explicitOrderStage = toSafeProjectText(input.projectStage || input.orderStage);
  const initialOrderStage = explicitOrderStage
    ? resolveProjectPipelineStage({ projectStage: explicitOrderStage })
    : input.stage
    ? resolveProjectPipelineStage({ stage: input.stage, status: input.status })
    : advanceAmount > 0
    ? "advance_received"
    : "order_confirmed";
  const paymentSummary = calculateProjectPaymentSummary({
    project: {
      ...input,
      orderValue,
      advanceAmount,
      balanceAmount,
      paymentStatus: input.paymentStatus || fallbackPaymentStatus,
    },
    payments: [],
  });
  const project = {
    projectId: toSafeProjectText(input.projectId),
    workspaceUid: toSafeProjectText(input.workspaceUid),
    opportunityId: toSafeProjectText(
      input.opportunityId ||
        sourceOpportunity.opportunityId ||
        sourceOpportunity.id ||
        sourceQuote.opportunityId
    ),
    quoteId,
    quoteNo,
    quoteType: toSafeProjectText(
      input.quoteType || sourceQuote.quoteType || sourceQuote.quotationType
    ),
    customerId: toSafeProjectText(
      input.customerId || sourceQuote.customerId || sourceOpportunity.customerId
    ),
    customerName: toSafeProjectText(
      input.customerName ||
        sourceQuote.customerName ||
        sourceOpportunity.customerName ||
        sourceQuote.customerDisplayName
    ),
    customerPhone: toSafeProjectText(
      input.customerPhone ||
        sourceQuote.customerPhone ||
        sourceQuote.normalizedPhone ||
        sourceOpportunity.customerPhone
    ),
    contactPersonName: toSafeProjectText(
      input.contactPersonName ||
        sourceQuote.contactPersonName ||
        sourceOpportunity.contactPersonName
    ),
    contactPersonPhone: toSafeProjectText(
      input.contactPersonPhone ||
        sourceQuote.contactPersonPhone ||
        sourceOpportunity.contactPersonPhone
    ),
    siteId: toSafeProjectText(input.siteId || sourceQuote.siteId || sourceOpportunity.siteId),
    siteName: toSafeProjectText(
      input.siteName ||
        sourceQuote.siteName ||
        sourceQuote.projectName ||
        sourceOpportunity.siteName
    ),
    siteAddress: toSafeProjectText(
      input.siteAddress ||
        sourceQuote.siteAddress ||
        sourceOpportunity.siteAddress ||
        sourceOpportunity.contact?.address ||
        sourceOpportunity.address
    ),
    orderValue,
    advanceAmount,
    balanceAmount,
    paymentStatus: normalizePaymentStatus(input.paymentStatus || fallbackPaymentStatus),
    paymentSummary,
    pipelineId: "orders",
    projectStage: initialOrderStage,
    orderStage: initialOrderStage,
    stage: normalizeProjectStage(
      input.stage ||
        mapOrderPipelineStageToLegacyProjectStage(initialOrderStage) ||
        (isChecklistComplete
          ? PROJECT_STAGE_FINAL_MEASUREMENT
          : PROJECT_STAGE_ORDER_HANDOVER)
    ),
    status: toSafeProjectText(input.status || "open") || "open",
    projectOwnerUid: toSafeProjectText(input.projectOwnerUid),
    projectOwnerName: toSafeProjectText(input.projectOwnerName),
    salesOwnerUid: toSafeProjectText(
      input.salesOwnerUid ||
        sourceOpportunity.ownerUid ||
        sourceQuote.ownerUid ||
        sourceQuote.createdBy
    ),
    salesOwnerName: toSafeProjectText(
      input.salesOwnerName ||
        sourceOpportunity.ownerName ||
        sourceQuote.ownerName ||
        sourceQuote.createdByName
    ),
    expectedInstallationDate: input.expectedInstallationDate || null,
    installationScheduledAt: input.installationScheduledAt || null,
    installationCompletedAt: input.installationCompletedAt || null,
    installationStatus: normalizeProjectInstallationStatus(
      input.installationStatus ||
        (input.installationCompletedAt
          ? PROJECT_INSTALLATION_STATUS_COMPLETED
          : input.installationScheduledAt || input.expectedInstallationDate
          ? PROJECT_INSTALLATION_STATUS_SCHEDULED
          : PROJECT_INSTALLATION_STATUS_NOT_SCHEDULED)
    ),
    expectedInstallationDateMissing: !input.expectedInstallationDate,
    productionDueAt: input.productionDueAt || null,
    materialDueAt: input.materialDueAt || null,
    handoverChecklist: checklist,
    checklist: executionChecklist,
    checklistSummary,
    snagSummary: normalizeProjectSnagSummary(input.snagSummary),
    hasOpenSnags: normalizeProjectSnagSummary(input.snagSummary).openCount > 0,
    openSnagCount: normalizeProjectSnagSummary(input.snagSummary).openCount,
    nextSnagDueAt: normalizeProjectSnagSummary(input.snagSummary).nextDueAt || null,
    reviewSummary: normalizeProjectReviewSummary(input.reviewSummary),
    hasPendingBalance: paymentSummary.balanceAmount > 0,
    handoverNotes: toSafeProjectText(input.handoverNotes),
    nextActionText: toSafeProjectText(input.nextActionText),
    nextActionDueAt: input.nextActionDueAt || null,
    nextActionOwnerUid: toSafeProjectText(input.nextActionOwnerUid || input.projectOwnerUid),
    nextActionOwnerName: toSafeProjectText(input.nextActionOwnerName || input.projectOwnerName),
    nextActionMissing: !toSafeProjectText(input.nextActionText),
    lastActivityAt: input.lastActivityAt || input.updatedAt || input.createdAt || null,
    lastActivityType: toSafeProjectText(input.lastActivityType || "project_created"),
    lastActivityLabel: toSafeProjectText(input.lastActivityLabel || "Order handover created"),
    createdAt: input.createdAt || null,
    createdBy: toSafeProjectText(input.createdBy),
    updatedAt: input.updatedAt || null,
    updatedBy: toSafeProjectText(input.updatedBy),
  };
  return {
    ...project,
    projectTitle: buildProjectTitle(project),
  };
};

export const normalizeOpportunityStage = (value) => {
  const key = normalizeCrmKey(value);
  const legacyMap = {
    quote_given: "quotation_sent",
    quotation_sent: "quotation_sent",
    measurement_pending: "site_visit_scheduled",
    follow_up: "negotiation_decision",
    design_discussion: "selection_design",
    negotiation: "negotiation_decision",
    advance_received: "order_confirmed",
    materials_ordered: "order_confirmed",
    stitching_started: "order_confirmed",
    in_production: "order_confirmed",
    ready_for_installation: "order_confirmed",
    ready_for_dispatch: "order_confirmed",
    installed: CRM_OPPORTUNITY_STATUS_WON,
    delivered_installed: CRM_OPPORTUNITY_STATUS_WON,
    completed: CRM_OPPORTUNITY_STATUS_WON,
    cancelled: CRM_OPPORTUNITY_STATUS_LOST,
    canceled: CRM_OPPORTUNITY_STATUS_LOST,
  };
  const mappedKey = legacyMap[key] || key;
  return CRM_OPPORTUNITY_STAGES.some((stage) => stage.key === mappedKey)
    ? mappedKey
    : "new_lead";
};

export const getOpportunityStageLabel = (stage) =>
  CRM_OPPORTUNITY_STAGES.find((item) => item.key === normalizeOpportunityStage(stage))
    ?.label || "New Lead";

export const normalizeOpportunityStatus = (value) => {
  const key = normalizeCrmKey(value);
  if (["won", "installed", "completed", "closed_won"].includes(key)) {
    return CRM_OPPORTUNITY_STATUS_WON;
  }
  if (["lost", "cancelled", "canceled", "closed_lost"].includes(key)) {
    return CRM_OPPORTUNITY_STATUS_LOST;
  }
  if (["nurture", "future", "on_hold"].includes(key)) {
    return CRM_OPPORTUNITY_STATUS_NURTURE;
  }
  return CRM_OPPORTUNITY_STATUS_OPEN;
};

const normalizeSalesPipelineStage = (stage = {}, fallbackStage = null, fallbackIndex = 0) => {
  const fallback = fallbackStage || {};
  const rawId =
    stage.stageId ||
    stage.id ||
    stage.key ||
    fallback.stageId ||
    fallback.id ||
    fallback.key ||
    createClientId("sales-stage");
  const stageId = normalizeCrmKey(rawId) || String(rawId || "").trim();
  const normalizedStageId = stageId || createClientId("sales-stage");
  const label = String(stage.label || stage.name || fallback.label || "New Stage").trim() || "New Stage";
  return {
    stageId: normalizedStageId,
    id: normalizedStageId,
    label,
    order: Number.isFinite(Number(stage.order))
      ? Number(stage.order)
      : Number.isFinite(Number(fallback.order))
      ? Number(fallback.order)
      : (fallbackIndex + 1) * 10,
    active: stage.active === false ? false : fallback.active === false ? false : true,
    color: String(stage.color || fallback.color || SALES_PIPELINE_STAGE_COLORS[normalizedStageId] || "#64748b"),
    isWon:
      stage.isWon === true ||
      fallback.isWon === true ||
      normalizedStageId === CRM_OPPORTUNITY_STATUS_WON,
    isLost:
      stage.isLost === true ||
      fallback.isLost === true ||
      normalizedStageId === CRM_OPPORTUNITY_STATUS_LOST,
    isNurture:
      stage.isNurture === true ||
      fallback.isNurture === true ||
      normalizedStageId === CRM_OPPORTUNITY_STATUS_NURTURE,
    systemStage: stage.systemStage === true || fallback.systemStage === true,
  };
};

export const normalizeSalesPipeline = (pipeline = {}) => {
  const incomingStages = Array.isArray(pipeline?.stages) ? pipeline.stages : [];
  const normalizedMap = new Map();

  DEFAULT_SALES_PIPELINE_STAGES.forEach((stage, index) => {
    const incoming = incomingStages.find(
      (item) => normalizeCrmKey(item?.stageId || item?.id || item?.key) === stage.stageId
    );
    const normalized = normalizeSalesPipelineStage(incoming || stage, stage, index);
    normalizedMap.set(normalized.stageId, normalized);
  });

  incomingStages.forEach((stage, index) => {
    const normalized = normalizeSalesPipelineStage(stage, null, DEFAULT_SALES_PIPELINE_STAGES.length + index);
    if (!normalizedMap.has(normalized.stageId)) {
      normalizedMap.set(normalized.stageId, normalized);
    }
  });

  const stages = Array.from(normalizedMap.values()).sort(
    (a, b) => Number(a.order || 0) - Number(b.order || 0)
  );

  return {
    pipelineId: String(pipeline.pipelineId || "sales"),
    name: String(pipeline.name || "Sales Pipeline"),
    type: "sales",
    isDefault: pipeline.isDefault !== false,
    stages,
  };
};

export const getActiveSalesPipelineStages = (pipeline = {}) =>
  normalizeSalesPipeline(pipeline).stages.filter((stage) => stage.active !== false);

export const getSalesPipelineStage = (pipeline = {}, stageId = "") => {
  const rawStageId = normalizeCrmKey(stageId);
  const normalized = normalizeOpportunityStage(stageId);
  const safePipeline = normalizeSalesPipeline(pipeline);
  return (
    safePipeline.stages.find((stage) => stage.stageId === rawStageId || stage.id === rawStageId) ||
    safePipeline.stages.find((stage) => stage.stageId === normalized) ||
    safePipeline.stages.find((stage) => stage.stageId === stageId) ||
    safePipeline.stages[0] ||
    null
  );
};

export const createSalesPipelineStageDraft = (label = "New Stage") => {
  const id = createClientId("sales_stage");
  return normalizeSalesPipelineStage({
    stageId: id,
    label,
    order: Date.now(),
    active: true,
    color: "#64748b",
    systemStage: false,
  });
};

const normalizeOrderPipelineStage = (stage = {}, fallbackStage = null, fallbackIndex = 0) => {
  const fallback = fallbackStage || {};
  const rawId =
    stage.stageId ||
    stage.id ||
    stage.key ||
    fallback.stageId ||
    fallback.id ||
    fallback.key ||
    createClientId("order-stage");
  const stageId = normalizeCrmKey(rawId) || String(rawId || "").trim();
  const normalizedStageId = stageId || createClientId("order-stage");
  const label = String(stage.label || stage.name || fallback.label || "New Stage").trim() || "New Stage";
  return {
    stageId: normalizedStageId,
    id: normalizedStageId,
    label,
    order: Number.isFinite(Number(stage.order))
      ? Number(stage.order)
      : Number.isFinite(Number(fallback.order))
      ? Number(fallback.order)
      : (fallbackIndex + 1) * 10,
    active: stage.active === false ? false : fallback.active === false ? false : true,
    color: String(stage.color || fallback.color || ORDER_PIPELINE_STAGE_COLORS[normalizedStageId] || "#64748b"),
    isClosed:
      stage.isClosed === true ||
      fallback.isClosed === true ||
      normalizedStageId === "project_closed",
    isCancelled:
      stage.isCancelled === true ||
      fallback.isCancelled === true ||
      normalizedStageId === "cancelled",
    systemStage: stage.systemStage === true || fallback.systemStage === true,
  };
};

export const normalizeOrderPipeline = (pipeline = {}) => {
  const incomingStages = Array.isArray(pipeline?.stages) ? pipeline.stages : [];
  const normalizedMap = new Map();

  DEFAULT_ORDER_PIPELINE_STAGES.forEach((stage, index) => {
    const incoming = incomingStages.find(
      (item) => normalizeCrmKey(item?.stageId || item?.id || item?.key) === stage.stageId
    );
    const normalized = normalizeOrderPipelineStage(incoming || stage, stage, index);
    normalizedMap.set(normalized.stageId, normalized);
  });

  incomingStages.forEach((stage, index) => {
    const normalized = normalizeOrderPipelineStage(stage, null, DEFAULT_ORDER_PIPELINE_STAGES.length + index);
    if (!normalizedMap.has(normalized.stageId)) {
      normalizedMap.set(normalized.stageId, normalized);
    }
  });

  const stages = Array.from(normalizedMap.values()).sort(
    (a, b) => Number(a.order || 0) - Number(b.order || 0)
  );

  return {
    pipelineId: "orders",
    name: String(pipeline.name || "Orders Pipeline"),
    type: "orders",
    isDefault: pipeline.isDefault !== false,
    stages,
  };
};

export const getActiveOrderPipelineStages = (pipeline = {}) =>
  normalizeOrderPipeline(pipeline).stages.filter((stage) => stage.active !== false);

export const getOrderPipelineStage = (pipeline = {}, stageId = "") => {
  const rawStageId = normalizeCrmKey(stageId);
  const safePipeline = normalizeOrderPipeline(pipeline);
  return (
    safePipeline.stages.find((stage) => stage.stageId === rawStageId || stage.id === rawStageId) ||
    safePipeline.stages.find((stage) => stage.stageId === stageId) ||
    safePipeline.stages[0] ||
    null
  );
};

export const createOrderPipelineStageDraft = (label = "New Stage") => {
  const id = createClientId("order_stage");
  return normalizeOrderPipelineStage({
    stageId: id,
    label,
    order: Date.now(),
    active: true,
    color: "#64748b",
    systemStage: false,
  });
};

export const getTaskStatus = (task = {}) => {
  const key = normalizeCrmKey(task?.status);
  if (["done", "complete", CRM_TASK_STATUS_COMPLETED].includes(key)) {
    return CRM_TASK_STATUS_COMPLETED;
  }
  if (["cancelled", "canceled", CRM_TASK_STATUS_CANCELLED].includes(key)) {
    return CRM_TASK_STATUS_CANCELLED;
  }
  return CRM_TASK_STATUS_OPEN;
};

export const isTaskOverdue = (task = {}, now = Date.now()) => {
  if (getTaskStatus(task) !== CRM_TASK_STATUS_OPEN) return false;
  const dueAt = normalizeCrmDate(task?.dueAt || task?.dueDate || task?.nextActionDueAt);
  return Boolean(dueAt && dueAt.getTime() < Number(now || Date.now()));
};

export const getNextActionStatus = (opportunity = {}, now = Date.now()) => {
  const text = String(
    opportunity?.nextActionText || opportunity?.nextAction || ""
  ).trim();
  const dueAt = normalizeCrmDate(
    opportunity?.nextActionDueAt || opportunity?.nextActionDate || null
  );
  if (!text) return "missing";
  if (!dueAt) return "no_date";
  const dueMillis = dueAt.getTime();
  const nowMillis = Number(now || Date.now());
  if (dueMillis < nowMillis) return "overdue";
  const dueKey = dueAt.toISOString().slice(0, 10);
  const todayKey = new Date(nowMillis).toISOString().slice(0, 10);
  return dueKey === todayKey ? "due_today" : "upcoming";
};

export const buildOpportunityDisplayTitle = (opportunity = {}) =>
  String(
    opportunity?.customerName ||
      opportunity?.contactPersonName ||
      opportunity?.siteName ||
      opportunity?.title ||
      "Untitled opportunity"
  ).trim();

export const buildCrmTaskTitle = (task = {}) => {
  const type = normalizeCrmKey(task?.taskType || task?.type);
  const typeLabels = {
    call: "Call customer",
    whatsapp: "WhatsApp follow-up",
    site_visit: "Site visit",
    measurement: "Measurement",
    quotation: "Prepare quotation",
    follow_up: "Follow-up",
    payment: "Payment follow-up",
    installation: "Installation",
  };
  return (
    String(task?.title || "").trim() ||
    typeLabels[type] ||
    "Follow-up"
  );
};

export const buildActivityLogEntry = (input = {}) => {
  const activityType = CRM_ACTIVITY_TYPES.includes(normalizeCrmKey(input?.activityType))
    ? normalizeCrmKey(input.activityType)
    : "note";
  const relatedType = String(input?.relatedType || "").trim() || "opportunity";
  const relatedId = String(input?.relatedId || "").trim();
  const projectId = String(
    input?.projectId || (normalizeCrmKey(relatedType) === "project" ? relatedId : "")
  ).trim();
  return {
    activityId:
      String(input?.activityId || "").trim() || createClientId("crm-activity"),
    workspaceUid: String(input?.workspaceUid || "").trim(),
    relatedType,
    relatedId,
    projectId,
    customerId: String(input?.customerId || "").trim(),
    customerName: String(input?.customerName || "").trim(),
    opportunityId: String(input?.opportunityId || "").trim(),
    quoteId: String(input?.quoteId || "").trim(),
    quoteNo: String(input?.quoteNo || "").trim(),
    paymentId: String(input?.paymentId || "").trim(),
    trackingItemId: String(input?.trackingItemId || "").trim(),
    materialId: String(input?.materialId || "").trim(),
    activityType,
    title: String(input?.title || "").trim() || "CRM activity",
    description: String(input?.description || "").trim(),
    createdAt: input?.createdAt || null,
    createdBy: String(input?.createdBy || "").trim(),
    createdByName: String(input?.createdByName || "").trim(),
  };
};

const normalizeExecutionSpecAttributes = (attributes = {}) => {
  if (!attributes || typeof attributes !== "object" || Array.isArray(attributes)) {
    return {};
  }

  return Object.entries(attributes).reduce((accumulator, [key, value]) => {
    const nextKey = String(key || "").trim();
    if (!nextKey) return accumulator;

    accumulator[nextKey] =
      value == null
        ? ""
        : typeof value === "string"
        ? value
        : String(value);
    return accumulator;
  }, {});
};

const normalizeExecutionSpecAttachment = (attachment = {}) => ({
  id:
    String(attachment?.id || "").trim() ||
    createClientId("exec-attachment"),
  name:
    String(attachment?.name || attachment?.filename || "").trim() ||
    "Attachment",
  filename: String(attachment?.filename || attachment?.name || "attachment").trim(),
  url: String(attachment?.url || attachment?.downloadURL || attachment?.downloadUrl || "").trim(),
  downloadURL: String(attachment?.downloadURL || attachment?.downloadUrl || attachment?.url || "").trim(),
  thumbnailDownloadURL: String(
    attachment?.thumbnailDownloadURL ||
      attachment?.thumbnailUrl ||
      attachment?.thumbnailURL ||
      ""
  ).trim(),
  thumbnailFullPath: String(
    attachment?.thumbnailFullPath || attachment?.thumbnailStoragePath || ""
  ).trim(),
  path: String(attachment?.path || attachment?.storagePath || "").trim(),
  storagePath: String(attachment?.storagePath || attachment?.path || "").trim(),
  fullPath: String(attachment?.fullPath || attachment?.path || attachment?.storagePath || "").trim(),
  fileId: String(attachment?.fileId || "").trim(),
  imageId: String(attachment?.imageId || attachment?.id || "").trim(),
  contentType: String(attachment?.contentType || "").trim(),
  sizeBytes: Number(attachment?.sizeBytes || 0),
  thumbnailSizeBytes: Number(attachment?.thumbnailSizeBytes || 0),
  originalSizeBytes: Number(attachment?.originalSizeBytes || 0),
  optimizedSizeBytes: Number(attachment?.optimizedSizeBytes || 0),
  compressionRatio: Number(attachment?.compressionRatio || 0),
  width: Number(attachment?.width || 0),
  height: Number(attachment?.height || 0),
  originalWidth: Number(attachment?.originalWidth || 0),
  originalHeight: Number(attachment?.originalHeight || 0),
  uploadStatus: String(attachment?.uploadStatus || "").trim(),
  compressionStatus: String(attachment?.compressionStatus || "").trim(),
  thumbnailStatus: String(attachment?.thumbnailStatus || "").trim(),
  ownerType: String(attachment?.ownerType || "").trim(),
  workspaceUid: String(attachment?.workspaceUid || "").trim(),
  leadId: String(attachment?.leadId || "").trim(),
  ownerId: String(attachment?.ownerId || "").trim(),
  removedFromRecord: attachment?.removedFromRecord === true,
  archived: attachment?.archived === true,
  uploadedAt: attachment?.uploadedAt || null,
  uploadedBy: String(attachment?.uploadedBy || attachment?.uploadedByUid || "").trim(),
  uploadedByName: String(attachment?.uploadedByName || "").trim(),
});

export const normalizeExecutionSpecAttachments = (attachments = []) =>
  (Array.isArray(attachments) ? attachments : [])
    .filter(Boolean)
    .map((attachment) => normalizeExecutionSpecAttachment(attachment));

export const normalizeExecutionTemplateItemType = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const toExecutionTemplateDraftString = (value = "") =>
  value == null ? "" : String(value);

export const createExecutionTemplateFieldDraft = (
  key = "",
  defaultValue = ""
) => ({
  id: createClientId("exec-template-field"),
  key: toExecutionTemplateDraftString(key),
  defaultValue: toExecutionTemplateDraftString(defaultValue),
});

const normalizeExecutionTemplateFieldDraft = (field = {}) => {
  if (typeof field === "string") {
    return createExecutionTemplateFieldDraft(field, "");
  }

  return {
    id:
      String(field?.id || "").trim() ||
      createClientId("exec-template-field"),
    key: toExecutionTemplateDraftString(
      field?.key ?? field?.name ?? field?.label ?? ""
    ),
    defaultValue:
      field?.defaultValue == null
        ? field?.value == null
          ? ""
          : String(field.value)
        : String(field.defaultValue),
  };
};

const normalizeExecutionTemplateField = (field = {}) => {
  const draftField = normalizeExecutionTemplateFieldDraft(field);
  return {
    ...draftField,
    key: String(draftField?.key || "").trim(),
  };
};

export const createExecutionSpecItem = (overrides = {}) => ({
  id: String(overrides?.id || "").trim() || createClientId("exec-item"),
  itemType: String(overrides?.itemType || "").trim(),
  itemName: String(overrides?.itemName || "").trim(),
  attributes: normalizeExecutionSpecAttributes(overrides?.attributes),
  attachments: normalizeExecutionSpecAttachments(overrides?.attachments),
  createdAt: overrides?.createdAt || null,
  updatedAt: overrides?.updatedAt || null,
});

export const normalizeExecutionSpecs = (items = []) =>
  (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .map((item) => createExecutionSpecItem(item));

export const createExecutionTemplateDraft = (
  name = "",
  itemType = "",
  fields = []
) => ({
  id: createClientId("exec-template"),
  name: toExecutionTemplateDraftString(name),
  itemType: toExecutionTemplateDraftString(itemType || name),
  fields: (Array.isArray(fields) ? fields : [])
    .map((field) => normalizeExecutionTemplateFieldDraft(field)),
  createdAt: null,
  updatedAt: null,
});

export const normalizeExecutionTemplateDrafts = (templates = []) =>
  (Array.isArray(templates) ? templates : [])
    .filter(Boolean)
    .map((template) => ({
      id: String(template?.id || "").trim() || createClientId("exec-template"),
      name: toExecutionTemplateDraftString(template?.name ?? ""),
      itemType: toExecutionTemplateDraftString(
        template?.itemType ?? template?.name ?? ""
      ),
      fields: (Array.isArray(template?.fields) ? template.fields : [])
        .map((field) => normalizeExecutionTemplateFieldDraft(field)),
      createdAt: template?.createdAt || null,
      updatedAt: template?.updatedAt || null,
    }));

export const normalizeExecutionTemplates = (templates = []) =>
  normalizeExecutionTemplateDrafts(templates).map((template) => ({
    ...template,
    name: String(template?.name || "").trim(),
    itemType: normalizeExecutionTemplateItemType(
      template?.itemType || template?.name || ""
    ),
    fields: (Array.isArray(template?.fields) ? template.fields : [])
      .map((field) => normalizeExecutionTemplateField(field))
      .filter((field) => String(field?.key || "").trim()),
  }));

const createChecklistNode = (
  label = "",
  prefix = "crm-check",
  overrides = {}
) => {
  const completed = getChecklistCompletionValue(overrides);

  return {
    id: overrides.id || createClientId(prefix),
    templateId: overrides.templateId || "",
    label: String(label ?? ""),
    title: String(overrides.title ?? label ?? ""),
    done: completed,
    completed,
    checked: completed,
    assignedToUserId: String(overrides.assignedToUserId || ""),
    assignedToNameSnapshot: String(
      overrides.assignedToNameSnapshot || overrides.assignedTo || ""
    ),
    assignedAt: overrides.assignedAt || null,
    createdAt: overrides.createdAt || null,
    createdBy: overrides.createdBy || null,
    updatedAt: overrides.updatedAt || null,
    completedAt: completed ? overrides.completedAt || null : null,
    completedBy: completed ? overrides.completedBy || null : null,
    notes: String(overrides.notes || ""),
    children: Array.isArray(overrides.children) ? overrides.children : [],
  };
};

const toDateInputString = (value) => {
  if (!value) return "";

  if (typeof value?.toDate === "function") {
    return toDateInputString(value.toDate());
  }

  const date =
    value instanceof Date
      ? new Date(value.getTime())
      : typeof value === "number"
      ? new Date(value)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeReminderRepeatKey = (value) => {
  const nextValue = String(value || "").trim();
  return CRM_REMINDER_REPEAT_OPTIONS.some((option) => option.key === nextValue)
    ? nextValue
    : "daily";
};

const normalizeReminderTime = (value) => {
  const nextValue = String(value || "").trim();
  return /^\d{2}:\d{2}$/.test(nextValue)
    ? nextValue
    : DEFAULT_CRM_REMINDER_TIME;
};

const normalizeReminderStatus = (status, enabled) => {
  if (status === CRM_REMINDER_STATUS_DONE) {
    return CRM_REMINDER_STATUS_DONE;
  }

  return enabled ? CRM_REMINDER_STATUS_ACTIVE : CRM_REMINDER_STATUS_DISABLED;
};

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return undefined;
};

const getChecklistCompletionValue = (item = {}) =>
  normalizeBoolean(item?.completed) === true ||
  normalizeBoolean(item?.done) === true ||
  normalizeBoolean(item?.checked) === true ||
  normalizeBoolean(item?.isComplete) === true;

const getChecklistChildren = (item) => {
  if (Array.isArray(item?.children)) return item.children;
  if (Array.isArray(item?.subItems)) return item.subItems;
  if (Array.isArray(item?.subChecklist)) return item.subChecklist;
  return [];
};

const normalizeReminderConfigEntry = (type, config = {}, fallbackDate = "") => {
  const reminderDate = String(config?.reminderDate ?? fallbackDate ?? "");
  const reminderEnabledValue = normalizeBoolean(config?.reminderEnabled);
  const enabledValue = normalizeBoolean(config?.enabled);
  const enabled =
    reminderEnabledValue !== undefined
      ? reminderEnabledValue
      : enabledValue !== undefined
      ? enabledValue
      : Boolean(reminderDate);

  return {
    type,
    enabled,
    reminderEnabled: enabled,
    reminderDate,
    reminderTime: normalizeReminderTime(config?.reminderTime),
    repeatKey: normalizeReminderRepeatKey(config?.repeatKey),
    status: normalizeReminderStatus(config?.status, enabled),
    completedAt: config?.completedAt || null,
  };
};

const createStage = ({
  id,
  label,
  color,
  confirmsQuote = false,
  ordersMaterial = false,
  marksComplete = false,
  isCancelled = false,
  defaultChecklist = [],
}) => ({
  id,
  label,
  color,
  confirmsQuote,
  ordersMaterial,
  marksComplete,
  isCancelled,
  defaultChecklist,
});

const DEFAULT_CURTAIN_STAGES = [
  createStage({
    id: "quote_given",
    label: "Quote Given",
    color: "#2563eb",
    defaultChecklist: [
      { label: "Site measurements verified" },
      { label: "Client approved quote" },
    ],
  }),
  createStage({
    id: "measurement_pending",
    label: "Measurement Pending",
    color: "#38bdf8",
    defaultChecklist: [
      { label: "Schedule site visit" },
      { label: "Collect final dimensions" },
    ],
  }),
  createStage({
    id: "follow_up",
    label: "Follow-up",
    color: "#8b5cf6",
    defaultChecklist: [
      { label: "Share revisions with client" },
      { label: "Confirm decision timeline" },
    ],
  }),
  createStage({
    id: "advance_received",
    label: "Advance Received",
    color: "#6366f1",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Advance payment recorded" },
      { label: "Final fabric selection approved" },
    ],
  }),
  createStage({
    id: "materials_ordered",
    label: "Material Ordered",
    color: "#f59e0b",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Fabric ordered" },
      { label: "Blinds / accessories ordered" },
      { label: "Vendor ETA confirmed" },
    ],
  }),
  createStage({
    id: "stitching_started",
    label: "Stitching Started",
    color: "#a855f7",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Fabric received" },
      { label: "Stitching in progress" },
    ],
  }),
  createStage({
    id: "ready_for_installation",
    label: "Ready For Installation",
    color: "#10b981",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Final QC completed" },
      { label: "Installation team scheduled" },
    ],
  }),
  createStage({
    id: "installed",
    label: "Installed",
    color: "#059669",
    confirmsQuote: true,
    marksComplete: true,
    defaultChecklist: [
      { label: "Site installation completed" },
      { label: "Client handover done" },
    ],
  }),
  createStage({
    id: "cancelled",
    label: "Cancelled",
    color: "#ef4444",
    isCancelled: true,
    defaultChecklist: [],
  }),
];

const DEFAULT_GENERAL_STAGES = [
  createStage({
    id: "quote_given",
    label: "Quotation Sent",
    color: "#2563eb",
    defaultChecklist: [
      { label: "Scope shared with client" },
      { label: "Quotation mailed to client" },
    ],
  }),
  createStage({
    id: "design_discussion",
    label: "Design Discussion",
    color: "#0ea5e9",
    defaultChecklist: [
      { label: "Reference images collected" },
      { label: "Material options presented" },
    ],
  }),
  createStage({
    id: "negotiation",
    label: "Negotiation",
    color: "#8b5cf6",
    defaultChecklist: [
      { label: "Commercial revisions shared" },
      { label: "Final approval pending" },
    ],
  }),
  createStage({
    id: "advance_received",
    label: "Advance Received",
    color: "#6366f1",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Advance payment recorded" },
      { label: "Production brief approved" },
    ],
  }),
  createStage({
    id: "in_production",
    label: "In Production",
    color: "#f59e0b",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Production plan released" },
      { label: "Materials procured" },
    ],
  }),
  createStage({
    id: "ready_for_dispatch",
    label: "Ready For Dispatch",
    color: "#10b981",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "QC completed" },
      { label: "Dispatch/installation scheduled" },
    ],
  }),
  createStage({
    id: "installed",
    label: "Delivered / Installed",
    color: "#059669",
    confirmsQuote: true,
    marksComplete: true,
    defaultChecklist: [
      { label: "Delivery or installation completed" },
      { label: "Client sign-off received" },
    ],
  }),
  createStage({
    id: "cancelled",
    label: "Cancelled",
    color: "#ef4444",
    isCancelled: true,
    defaultChecklist: [],
  }),
];

export const DEFAULT_CRM_PIPELINES = {
  [CURTAIN_QUOTE_TYPE]: {
    key: CURTAIN_QUOTE_TYPE,
    label: "Curtains Pipeline",
    stages: DEFAULT_CURTAIN_STAGES,
  },
  [GENERAL_QUOTE_TYPE]: {
    key: GENERAL_QUOTE_TYPE,
    label: "General Quotation Pipeline",
    stages: DEFAULT_GENERAL_STAGES,
  },
};

export const getQuoteTypeLabel = (quoteType) =>
  quoteType === GENERAL_QUOTE_TYPE ? "General Quotation" : "Curtains";

export const createDealChecklistItem = (label = "", overrides = {}) =>
  createChecklistNode(label, "crm-check", overrides);

const normalizeChecklistNode = (
  item,
  depth = 1,
  { templateMode = false, idPrefix } = {}
) => {
  if (!item || depth > CRM_CHECKLIST_MAX_DEPTH) return null;

  if (typeof item === "string") {
    const label = String(item).trim();
    if (!label) return null;
    return createChecklistNode(label, idPrefix || "crm-stage-task", {
      children: [],
    });
  }

  const children = getChecklistChildren(item)
    .map((child) =>
      normalizeChecklistNode(child, depth + 1, {
        templateMode,
        idPrefix,
      })
    )
    .filter(Boolean);
  const label = String(item?.label ?? item?.title ?? "");
  if (!label.trim() && !item?.id && children.length === 0) return null;
  const done = templateMode ? false : getChecklistCompletionValue(item);

  return createChecklistNode(label, idPrefix || "crm-check", {
    id: item?.id,
    templateId: item?.templateId || "",
    done,
    completed: done,
    checked: done,
    assignedToUserId: templateMode ? "" : item?.assignedToUserId || "",
    assignedToNameSnapshot: templateMode
      ? ""
      : item?.assignedToNameSnapshot || item?.assignedTo || "",
    assignedAt: templateMode ? null : item?.assignedAt || null,
    createdAt: templateMode ? null : item?.createdAt || null,
    createdBy: templateMode ? null : item?.createdBy || null,
    updatedAt: templateMode ? null : item?.updatedAt || null,
    completedAt: !templateMode && done ? item?.completedAt || null : null,
    completedBy: !templateMode && done ? item?.completedBy || null : null,
    notes: item?.notes || "",
    children,
  });
};

const syncChecklistNodeCompletion = (node) => {
  const normalizedChildren = getChecklistChildren(node)
    .map((child) => syncChecklistNodeCompletion(child))
    .filter(Boolean);

  const done =
    normalizedChildren.length > 0
      ? normalizedChildren.every((child) => child.done === true)
      : getChecklistCompletionValue(node);

  return {
    ...node,
    done,
    completed: done,
    checked: done,
    title: node?.title ?? node?.label ?? "",
    completedAt: done ? node?.completedAt || null : null,
    completedBy: done ? node?.completedBy || null : null,
    children: normalizedChildren,
  };
};

export const syncChecklistTreeCompletion = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) =>
    syncChecklistNodeCompletion(item)
  );

export const normalizeChecklistTemplate = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) =>
      normalizeChecklistNode(item, 1, {
        templateMode: true,
        idPrefix: "crm-stage-task",
      })
    )
    .filter(Boolean);

export const normalizeChecklistTree = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) =>
      normalizeChecklistNode(item, 1, {
        templateMode: false,
        idPrefix: "crm-check",
      })
    )
    .filter(Boolean)
    .map((item) => syncChecklistNodeCompletion(item));

const preserveChecklistDraftNode = (
  item,
  depth = 1,
  { templateMode = false, idPrefix } = {}
) => {
  if (!item || depth > CRM_CHECKLIST_MAX_DEPTH) return null;

  if (typeof item === "string") {
    return createChecklistNode(String(item), idPrefix || "crm-check", {
      children: [],
    });
  }

  const children = getChecklistChildren(item)
    .map((child) =>
      preserveChecklistDraftNode(child, depth + 1, {
        templateMode,
        idPrefix,
      })
    )
    .filter(Boolean);

  return createChecklistNode(item?.label ?? item?.title ?? "", idPrefix || "crm-check", {
    id: item?.id,
    templateId: item?.templateId || "",
    done: templateMode ? false : getChecklistCompletionValue(item),
    completed: templateMode ? false : getChecklistCompletionValue(item),
    checked: templateMode ? false : getChecklistCompletionValue(item),
    assignedToUserId: templateMode ? "" : item?.assignedToUserId || "",
    assignedToNameSnapshot: templateMode
      ? ""
      : item?.assignedToNameSnapshot || item?.assignedTo || "",
    assignedAt: templateMode ? null : item?.assignedAt || null,
    createdAt: templateMode ? null : item?.createdAt || null,
    createdBy: templateMode ? null : item?.createdBy || null,
    updatedAt: templateMode ? null : item?.updatedAt || null,
    completedAt: templateMode ? null : item?.completedAt || null,
    completedBy: templateMode ? null : item?.completedBy || null,
    notes: item?.notes || "",
    children,
  });
};

export const preserveChecklistDraftTree = (
  items = [],
  { templateMode = false, idPrefix } = {}
) =>
  (Array.isArray(items) ? items : [])
    .map((item) =>
      preserveChecklistDraftNode(item, 1, {
        templateMode,
        idPrefix: idPrefix || (templateMode ? "crm-stage-task" : "crm-check"),
      })
    )
    .filter(Boolean)
    .map((item) => syncChecklistNodeCompletion(item));

export const cloneChecklistTree = (
  items = [],
  {
    idPrefix = "crm-check",
    preserveIds = false,
    resetState = false,
    useTemplateIds = false,
  } = {}
) =>
  (Array.isArray(items) ? items : []).map((item) => {
    const children = cloneChecklistTree(getChecklistChildren(item), {
      idPrefix,
      preserveIds,
      resetState,
      useTemplateIds,
    });

    return createChecklistNode(item?.label || item?.title || "", idPrefix, {
      id: preserveIds ? item?.id : undefined,
      templateId:
        useTemplateIds === true
          ? item?.templateId || item?.id || ""
          : item?.templateId || "",
      done: resetState ? false : getChecklistCompletionValue(item),
      completed: resetState ? false : getChecklistCompletionValue(item),
      checked: resetState ? false : getChecklistCompletionValue(item),
      assignedToUserId: resetState ? "" : item?.assignedToUserId || "",
      assignedToNameSnapshot: resetState
        ? ""
        : item?.assignedToNameSnapshot || item?.assignedTo || "",
      assignedAt: resetState ? null : item?.assignedAt || null,
      createdAt: resetState ? null : item?.createdAt || null,
      createdBy: resetState ? null : item?.createdBy || null,
      updatedAt: resetState ? null : item?.updatedAt || null,
      completedAt: resetState ? null : item?.completedAt || null,
      completedBy: resetState ? null : item?.completedBy || null,
      notes: resetState ? "" : item?.notes || "",
      children,
    });
  });

const normalizeStage = (stage, fallbackStage) => {
  const base = fallbackStage || createStage({
    id: stage?.id || createClientId("crm-stage"),
    label: stage?.label || "New Stage",
    color: stage?.color || "#64748b",
  });

  return {
    ...base,
    ...stage,
    id: stage?.id || base.id,
    label:
      stage?.label != null
        ? String(stage.label)
        : String(base.label || "New Stage"),
    color: String(stage?.color || base.color || "#64748b"),
    confirmsQuote: stage?.confirmsQuote === true || base.confirmsQuote === true,
    ordersMaterial: false,
    marksComplete: stage?.marksComplete === true || base.marksComplete === true,
    isCancelled: stage?.isCancelled === true || base.isCancelled === true,
    defaultChecklist: normalizeChecklistTemplate(
      stage?.defaultChecklist ?? base.defaultChecklist
    ),
  };
};

export const normalizeCrmPipelines = (pipelines = {}) => {
  const next = {};

  CRM_PIPELINE_TYPES.forEach((type) => {
    const defaults = DEFAULT_CRM_PIPELINES[type];
    const incoming = pipelines?.[type] || {};
    const incomingStages = Array.isArray(incoming.stages) ? incoming.stages : [];

    next[type] = {
      key: type,
      label:
        incoming.label != null ? String(incoming.label) : defaults.label,
      stages:
        incomingStages.length > 0
          ? incomingStages.map((stage) =>
              normalizeStage(
                stage,
                defaults.stages.find((defaultStage) => defaultStage.id === stage?.id)
              )
            )
          : defaults.stages.map((stage) => normalizeStage(stage, stage)),
    };

    if (!next[type].stages.some((stage) => stage.isCancelled)) {
      next[type].stages.push(
        normalizeStage(
          createStage({
            id: "cancelled",
            label: "Cancelled",
            color: "#ef4444",
            isCancelled: true,
          })
        )
      );
    }
  });

  return next;
};

export const getCrmPipeline = (crmPipelines, quoteType) =>
  normalizeCrmPipelines(crmPipelines)[
    quoteType === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE
  ];

export const getCrmStage = (crmPipelines, quoteType, stageId) => {
  const pipeline = getCrmPipeline(crmPipelines, quoteType);
  return (
    pipeline.stages.find((stage) => stage.id === stageId) ||
    pipeline.stages[0] ||
    null
  );
};

export const getDefaultCrmStageId = (crmPipelines, quoteType) =>
  getCrmPipeline(crmPipelines, quoteType)?.stages?.[0]?.id || "quote_given";

export const createChecklistFromStage = (stage) =>
  cloneChecklistTree(stage?.defaultChecklist || [], {
    idPrefix: "crm-check",
    preserveIds: false,
    resetState: true,
    useTemplateIds: true,
  });

export const normalizeDealChecklist = (items = [], stage = null) => {
  const normalized = normalizeChecklistTree(items);

  if (normalized.length > 0) return normalized;
  return createChecklistFromStage(stage);
};

export const getChecklistNodeProgress = (node) => {
  const children = Array.isArray(node?.children) ? node.children : [];
  const childProgresses = children.map((child) => getChecklistNodeProgress(child));
  const childProgress = childProgresses.reduce(
    (sum, current) => {
      return {
        total: sum.total + current.total,
        completed: sum.completed + current.completed,
      };
    },
    { total: 0, completed: 0 }
  );

  const nodeDone = children.length > 0
    ? childProgresses.every((child) => child.done === true)
    : node?.done === true;

  return {
    total: 1 + childProgress.total,
    completed: (nodeDone ? 1 : 0) + childProgress.completed,
    done: nodeDone,
    partial:
      !nodeDone &&
      childProgress.completed > 0 &&
      childProgress.completed < childProgress.total + 1,
  };
};

export const getChecklistProgress = (items = []) => {
  const checklistItems = normalizeChecklistTree(items);
  const summary = checklistItems.reduce(
    (sum, item) => {
      const progress = getChecklistNodeProgress(item);
      return {
        total: sum.total + progress.total,
        completed: sum.completed + progress.completed,
      };
    },
    { total: 0, completed: 0 }
  );

  return {
    total: summary.total,
    completed: summary.completed,
  };
};

export const getChecklistSummary = (leadLike = {}) => {
  const sourceItems = Array.isArray(leadLike?.checklistItems) && leadLike.checklistItems.length > 0
    ? leadLike.checklistItems
    : Array.isArray(leadLike?.checklist) && leadLike.checklist.length > 0
    ? leadLike.checklist
    : Array.isArray(leadLike?.stageChecklist) && leadLike.stageChecklist.length > 0
    ? leadLike.stageChecklist
    : [];
  const progress = getChecklistProgress(sourceItems);

  return {
    total: progress.total,
    completed: progress.completed,
    label:
      progress.total > 0
        ? `${progress.completed}/${progress.total} complete`
        : "No checklist",
  };
};

export const createStageDraft = (label = "New Stage") =>
  createStage({
    id: createClientId("crm-stage"),
    label,
    color: "#64748b",
  });

export const createChecklistTemplateDraft = (label = "New Checklist Item") => ({
  ...createChecklistNode(label, "crm-stage-task"),
  done: false,
  completed: false,
  checked: false,
});

export const createChecklistChildDraft = (
  label = "New Checklist Item",
  { templateMode = false, ...overrides } = {}
) =>
  createChecklistNode(
    label,
    templateMode ? "crm-stage-task" : "crm-check",
    overrides
  );

const updateChecklistTreeInternal = (items, targetId, updater) => {
  let changed = false;
  const next = (Array.isArray(items) ? items : []).map((item) => {
    let updatedItem = item;

    if (item.id === targetId) {
      updatedItem = updater(item);
      changed = true;
    }

    const nextChildren = updateChecklistTreeInternal(
      getChecklistChildren(updatedItem),
      targetId,
      updater
    );

    if (nextChildren.changed) {
      updatedItem = {
        ...updatedItem,
        children: nextChildren.items,
      };
      changed = true;
    }

    return updatedItem;
  });

  return { items: next, changed };
};

export const updateChecklistNodeById = (items = [], nodeId, updater) =>
  syncChecklistTreeCompletion(
    updateChecklistTreeInternal(items, nodeId, updater).items
  );

const deleteChecklistTreeInternal = (items, targetId) => {
  let changed = false;
  const next = (Array.isArray(items) ? items : [])
    .filter((item) => {
      if (item.id === targetId) {
        changed = true;
        return false;
      }
      return true;
    })
    .map((item) => {
      const nextChildren = deleteChecklistTreeInternal(
        getChecklistChildren(item),
        targetId
      );
      if (nextChildren.changed) {
        changed = true;
        return {
          ...item,
          children: nextChildren.items,
        };
      }
      return item;
    });

  return { items: next, changed };
};

export const deleteChecklistNodeById = (items = [], nodeId) =>
  syncChecklistTreeCompletion(deleteChecklistTreeInternal(items, nodeId).items);

const insertChecklistChildInternal = (items, parentId, childNode, depth = 1) => {
  let changed = false;
  const next = (Array.isArray(items) ? items : []).map((item) => {
    if (item.id === parentId) {
      if (depth >= CRM_CHECKLIST_MAX_DEPTH) {
        return item;
      }

      changed = true;
      return {
        ...item,
        children: [...(item.children || []), childNode],
      };
    }

    const nextChildren = insertChecklistChildInternal(
      getChecklistChildren(item),
      parentId,
      childNode,
      depth + 1
    );

    if (nextChildren.changed) {
      changed = true;
      return {
        ...item,
        children: nextChildren.items,
      };
    }

    return item;
  });

  return { items: next, changed };
};

export const insertChecklistChildNode = (items = [], parentId, childNode) =>
  syncChecklistTreeCompletion(
    insertChecklistChildInternal(items, parentId, childNode).items
  );

const moveChecklistNodeInternal = (items, nodeId, direction) => {
  const list = Array.isArray(items) ? [...items] : [];
  const index = list.findIndex((item) => item.id === nodeId);

  if (index >= 0) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= list.length) {
      return { items: list, changed: false };
    }

    const [moved] = list.splice(index, 1);
    list.splice(targetIndex, 0, moved);
    return { items: list, changed: true };
  }

  let changed = false;
  const next = list.map((item) => {
    const nextChildren = moveChecklistNodeInternal(
      getChecklistChildren(item),
      nodeId,
      direction
    );

    if (nextChildren.changed) {
      changed = true;
      return {
        ...item,
        children: nextChildren.items,
      };
    }

    return item;
  });

  return { items: next, changed };
};

export const moveChecklistNodeById = (items = [], nodeId, direction) =>
  syncChecklistTreeCompletion(
    moveChecklistNodeInternal(items, nodeId, direction).items
  );

export const toggleChecklistNodeById = (
  items = [],
  nodeId,
  checked,
  completion = {}
) => {
  const completedAt = checked ? completion.completedAt || new Date() : null;
  const completedBy = checked ? completion.completedBy || null : null;
  const updatedAt = completion.updatedAt || new Date();
  const toggleNode = (node) => ({
    ...node,
    done: checked,
    completed: checked,
    checked,
    completedAt,
    completedBy,
    updatedAt,
    children: getChecklistChildren(node).map(toggleNode),
  });

  return syncChecklistTreeCompletion(
    updateChecklistTreeInternal(items, nodeId, toggleNode).items
  );
};

export const getStageSelectOptions = (crmPipelines, quoteType, currentStageId) => {
  const pipeline = getCrmPipeline(crmPipelines, quoteType);
  const stages = [...pipeline.stages];

  if (currentStageId && !stages.some((stage) => stage.id === currentStageId)) {
    stages.unshift(
      createStage({
        id: currentStageId,
        label: String(currentStageId || "Legacy Stage"),
        color: "#64748b",
      })
    );
  }

  return stages;
};

export const getStageFilterOptions = (crmPipelines, quoteTypeFilter = "all") => {
  const types =
    quoteTypeFilter === "all"
      ? CRM_PIPELINE_TYPES
      : [quoteTypeFilter === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE];

  return types.flatMap((type) =>
    getCrmPipeline(crmPipelines, type).stages.map((stage) => ({
      key: `${type}::${stage.id}`,
      quoteType: type,
      stageId: stage.id,
      label: `${getQuoteTypeLabel(type)} | ${stage.label}`,
      color: stage.color,
    }))
  );
};

export const normalizeCrmReminderConfigs = (
  reminderConfigs = {},
  {
    nextActionDate = null,
    installationDate = null,
  } = {}
) => {
  const safeConfigs =
    reminderConfigs && typeof reminderConfigs === "object"
      ? reminderConfigs
      : {};

  return {
    [CRM_REMINDER_TYPE_TASK_DUE]: normalizeReminderConfigEntry(
      CRM_REMINDER_TYPE_TASK_DUE,
      safeConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      toDateInputString(nextActionDate)
    ),
    [CRM_REMINDER_TYPE_INSTALLATION]: normalizeReminderConfigEntry(
      CRM_REMINDER_TYPE_INSTALLATION,
      safeConfigs[CRM_REMINDER_TYPE_INSTALLATION],
      toDateInputString(installationDate)
    ),
  };
};

export const getCrmReminderTypeLabel = (type) =>
  type === CRM_REMINDER_TYPE_INSTALLATION
    ? "Installation Reminder"
    : "Task Due Reminder";

export const getCrmReminderRepeatMinutes = (repeatKey) =>
  CRM_REMINDER_REPEAT_OPTIONS.find((option) => option.key === repeatKey)?.minutes ||
  1440;

const normalizeCrmFollowUpRepeat = (value) => {
  const nextValue = String(value || "").trim();
  return CRM_FOLLOW_UP_REPEAT_OPTIONS.some((option) => option.key === nextValue)
    ? nextValue
    : "none";
};

const normalizeCrmFollowUpStatus = (value) =>
  value === CRM_FOLLOW_UP_STATUS_DONE
    ? CRM_FOLLOW_UP_STATUS_DONE
    : CRM_FOLLOW_UP_STATUS_PENDING;

const normalizeCrmFollowUpPriority = (value) => {
  const nextValue = String(value || "").trim().toLowerCase();
  return CRM_FOLLOW_UP_PRIORITY_OPTIONS.some((option) => option.key === nextValue)
    ? nextValue
    : CRM_FOLLOW_UP_PRIORITY_MEDIUM;
};

const normalizeCrmFollowUpActor = (value = {}) => {
  if (typeof value === "string") {
    return {
      uid: "",
      name: value,
      email: "",
    };
  }

  return {
    uid: String(value?.uid || value?.completedByUid || value?.createdByUid || "").trim(),
    name: String(value?.name || value?.completedByName || value?.createdByName || "").trim(),
    email: String(value?.email || value?.completedByEmail || value?.createdByEmail || "").trim(),
  };
};

const sortCrmFollowUps = (followUps = []) =>
  [...followUps].sort((a, b) => {
    if (a.status !== b.status) {
      return a.status === CRM_FOLLOW_UP_STATUS_PENDING ? -1 : 1;
    }

    const aOrder = Number.isFinite(Number(a.order))
      ? Number(a.order)
      : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b.order))
      ? Number(b.order)
      : Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    const aDue = new Date(
      `${a.dueDate || "9999-12-31"}T${a.dueTime || DEFAULT_CRM_REMINDER_TIME}:00`
    ).getTime();
    const bDue = new Date(
      `${b.dueDate || "9999-12-31"}T${b.dueTime || DEFAULT_CRM_REMINDER_TIME}:00`
    ).getTime();

    if (aDue !== bDue) {
      return aDue - bDue;
    }

    const aUpdated = new Date(a.updatedAt || a.createdAt || 0).getTime() || 0;
    const bUpdated = new Date(b.updatedAt || b.createdAt || 0).getTime() || 0;
    return bUpdated - aUpdated;
  });

export const normalizeCrmFollowUps = (
  followUps = [],
  {
    nextAction = "",
    nextActionDate = null,
    reminderConfig = null,
  } = {}
) => {
  const normalizedItems = (Array.isArray(followUps) ? followUps : [])
    .filter(Boolean)
    .map((item, index) => {
      const id =
        String(item?.id || item?.sourceTaskId || "").trim() ||
        `crm-follow-up-${index + 1}`;
      const order = Number.isFinite(Number(item?.order))
        ? Number(item.order)
        : index + 1;
      const dueDate = toDateInputString(item?.dueDate || item?.nextActionDate || "");
      const status = normalizeCrmFollowUpStatus(item?.status);
      const explicitReminderEnabled = normalizeBoolean(
        item?.reminderEnabled ?? item?.enabled
      );

      return {
        id,
        order,
        priority: normalizeCrmFollowUpPriority(item?.priority),
        reminderId:
          String(item?.reminderId || item?.reminderDocId || item?.reminder_id || "").trim() ||
          id,
        title: String(item?.title || item?.name || "").trim(),
        description: String(item?.description || item?.notes || "").trim(),
        dueDate,
        dueTime: normalizeReminderTime(
          item?.dueTime ?? item?.reminderTime ?? DEFAULT_CRM_REMINDER_TIME
        ),
        repeat: normalizeCrmFollowUpRepeat(item?.repeat || item?.repeatKey),
        reminderEnabled:
          explicitReminderEnabled !== undefined
            ? explicitReminderEnabled
            : status !== CRM_FOLLOW_UP_STATUS_DONE && Boolean(dueDate),
        status,
        createdAt: item?.createdAt || null,
        updatedAt: item?.updatedAt || null,
        createdBy: normalizeCrmFollowUpActor(item?.createdBy),
        completedAt: item?.completedAt || null,
        completedBy: normalizeCrmFollowUpActor(item?.completedBy),
        attachments: normalizeCrmAttachments(item?.attachments),
      };
    })
    .filter((item) => item.title || item.dueDate || item.attachments.length > 0);

  if (normalizedItems.length > 0) {
    return sortCrmFollowUps(normalizedItems);
  }

  const legacyTitle = String(nextAction || "").trim();
  const legacyDueDate = toDateInputString(nextActionDate);

  if (!legacyTitle && !legacyDueDate) {
    return [];
  }

  return [
    {
      id: "legacy-follow-up",
      order: 1,
      priority: CRM_FOLLOW_UP_PRIORITY_MEDIUM,
      reminderId: "legacy-follow-up",
      title: legacyTitle || "Follow-up",
      description: "",
      dueDate: legacyDueDate,
      dueTime: normalizeReminderTime(reminderConfig?.reminderTime),
      repeat: normalizeCrmFollowUpRepeat(reminderConfig?.repeatKey),
      reminderEnabled:
        normalizeBoolean(reminderConfig?.reminderEnabled ?? reminderConfig?.enabled) ??
        Boolean(legacyDueDate),
      status:
        reminderConfig?.status === CRM_REMINDER_STATUS_DONE
          ? CRM_FOLLOW_UP_STATUS_DONE
          : CRM_FOLLOW_UP_STATUS_PENDING,
      createdAt: null,
      updatedAt: null,
      createdBy: normalizeCrmFollowUpActor({}),
      completedAt: reminderConfig?.completedAt || null,
      completedBy: normalizeCrmFollowUpActor({}),
      attachments: [],
    },
  ];
};

const toReminderDateTimeMillis = (dateValue = "", timeValue = "") => {
  const date = toDateInputString(dateValue);
  if (!date) return 0;
  const time = normalizeReminderTime(timeValue);
  const millis = new Date(`${date}T${time}:00`).getTime();
  return Number.isNaN(millis) ? 0 : millis;
};

const formatFollowUpDateTimeLabel = (followUp = {}) => {
  const millis = toReminderDateTimeMillis(followUp.dueDate, followUp.dueTime);
  if (!millis) return "";

  return new Date(millis).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const getFollowUpSummary = (leadLike = {}, { nowMillis = Date.now() } = {}) => {
  const sourceFollowUps = Array.isArray(leadLike)
    ? leadLike
    : Array.isArray(leadLike?.followUps)
    ? leadLike.followUps
    : Array.isArray(leadLike?.crm?.followUps)
    ? leadLike.crm.followUps
    : [];
  const normalizedFollowUps = normalizeCrmFollowUps(sourceFollowUps, {
    nextAction: leadLike?.nextAction || leadLike?.crm?.nextAction || "",
    nextActionDate: leadLike?.nextActionDate || leadLike?.crm?.nextActionDate || null,
    reminderConfig:
      leadLike?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
      leadLike?.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
      null,
  });
  const isDone = (followUp) =>
    followUp?.status === CRM_FOLLOW_UP_STATUS_DONE || Boolean(followUp?.completedAt);
  const pending = normalizedFollowUps.filter((followUp) => !isDone(followUp));
  const completed = normalizedFollowUps.length - pending.length;
  const overdue = pending.filter((followUp) => {
    const dueMillis = toReminderDateTimeMillis(followUp.dueDate, followUp.dueTime);
    return dueMillis > 0 && dueMillis < nowMillis;
  });
  const nextFollowUp =
    [...pending].sort((a, b) => {
      const aDue = toReminderDateTimeMillis(a.dueDate, a.dueTime) || Number.MAX_SAFE_INTEGER;
      const bDue = toReminderDateTimeMillis(b.dueDate, b.dueTime) || Number.MAX_SAFE_INTEGER;
      return aDue - bDue;
    })[0] || null;
  const nextFollowUpDateLabel = nextFollowUp ? formatFollowUpDateTimeLabel(nextFollowUp) : "";
  const nextFollowUpLabel = nextFollowUp
    ? `${nextFollowUp.title || "Follow-up"}${nextFollowUpDateLabel ? ` - ${nextFollowUpDateLabel}` : ""}`
    : "No follow-up set";

  return {
    total: normalizedFollowUps.length,
    pending: pending.length,
    completed,
    overdue: overdue.length,
    nextFollowUp,
    nextFollowUpLabel,
    label: `${pending.length} pending${overdue.length ? ` • ${overdue.length} overdue` : ""}`,
  };
};

export const getInstallationSummary = (leadLike = {}) => {
  const installationDate =
    toDateInputString(
      leadLike?.installationDate ||
        leadLike?.crm?.installationDate ||
        leadLike?.scheduledInstallationDate ||
        leadLike?.siteInstallDate ||
        leadLike?.installDate ||
        ""
    ) || "";
  const rawReminder =
    leadLike?.installationReminder ||
    leadLike?.crm?.installationReminder ||
    leadLike?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
    leadLike?.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
    leadLike?.reminderConfig ||
    leadLike?.crm?.reminderConfig ||
    {};
  const reminder = normalizeReminderConfigEntry(
    CRM_REMINDER_TYPE_INSTALLATION,
    rawReminder,
    installationDate
  );

  return {
    installationDate,
    installationLabel: installationDate || "-",
    reminderEnabled: reminder.enabled === true || reminder.reminderEnabled === true,
    reminderLabel:
      reminder.enabled === true || reminder.reminderEnabled === true
        ? "Reminder on"
        : "Reminder off",
  };
};

export const getPrimaryPendingCrmFollowUp = (followUps = []) =>
  sortCrmFollowUps(normalizeCrmFollowUps(followUps)).find(
    (item) => item.status !== CRM_FOLLOW_UP_STATUS_DONE
  ) || sortCrmFollowUps(normalizeCrmFollowUps(followUps))[0] || null;

export const getCrmFollowUpRepeatMinutes = (repeatKey) =>
  CRM_FOLLOW_UP_REPEAT_OPTIONS.find((option) => option.key === repeatKey)?.minutes ??
  0;

export const normalizeCrmAttachments = (attachments = []) =>
  (Array.isArray(attachments) ? attachments : [])
    .filter(Boolean)
    .map((attachment, index) => ({
      id: String(
        attachment?.id ||
          attachment?.storagePath ||
          attachment?.url ||
          attachment?.filename ||
          `crm-attachment-${index}`
      ),
      name: String(
        attachment?.name ||
          attachment?.filename ||
          attachment?.fileName ||
          "Attachment"
      ),
      filename: String(
        attachment?.filename ||
          attachment?.fileName ||
          attachment?.name ||
          "attachment"
      ),
      url: String(attachment?.url || attachment?.downloadUrl || ""),
      downloadURL: String(attachment?.downloadURL || attachment?.downloadUrl || attachment?.url || ""),
      thumbnailDownloadURL: String(
        attachment?.thumbnailDownloadURL ||
          attachment?.thumbnailUrl ||
          attachment?.thumbnailURL ||
          ""
      ),
      thumbnailFullPath: String(
        attachment?.thumbnailFullPath || attachment?.thumbnailStoragePath || ""
      ),
      storagePath: String(attachment?.storagePath || attachment?.path || ""),
      path: String(attachment?.path || attachment?.storagePath || ""),
      fullPath: String(attachment?.fullPath || attachment?.storagePath || attachment?.path || ""),
      fileId: String(attachment?.fileId || ""),
      imageId: String(attachment?.imageId || attachment?.id || ""),
      contentType: String(attachment?.contentType || attachment?.type || ""),
      sizeBytes: Number(attachment?.sizeBytes || attachment?.size || 0),
      thumbnailSizeBytes: Number(attachment?.thumbnailSizeBytes || 0),
      originalSizeBytes: Number(attachment?.originalSizeBytes || 0),
      optimizedSizeBytes: Number(attachment?.optimizedSizeBytes || 0),
      compressionRatio: Number(attachment?.compressionRatio || 0),
      width: Number(attachment?.width || 0),
      height: Number(attachment?.height || 0),
      originalWidth: Number(attachment?.originalWidth || 0),
      originalHeight: Number(attachment?.originalHeight || 0),
      uploadStatus: String(attachment?.uploadStatus || ""),
      compressionStatus: String(attachment?.compressionStatus || ""),
      thumbnailStatus: String(attachment?.thumbnailStatus || ""),
      ownerType: String(attachment?.ownerType || ""),
      workspaceUid: String(attachment?.workspaceUid || ""),
      leadId: String(attachment?.leadId || ""),
      ownerId: String(attachment?.ownerId || ""),
      removedFromRecord: attachment?.removedFromRecord === true,
      archived: attachment?.archived === true,
      uploadedAt: attachment?.uploadedAt || null,
      uploadedByUid: String(attachment?.uploadedByUid || ""),
      uploadedByName: String(
        attachment?.uploadedByName ||
          attachment?.uploadedBy ||
          ""
      ),
    }))
    .sort((a, b) => {
      const aTime = new Date(a.uploadedAt || 0).getTime() || 0;
      const bTime = new Date(b.uploadedAt || 0).getTime() || 0;
      return bTime - aTime;
    });

export const buildCrmContactFromQuote = (quote, quoteType, generalQuote = null) => {
  if (quoteType === GENERAL_QUOTE_TYPE) {
    const quoteFor = generalQuote?.quoteFor || quote?.generalQuote?.quoteFor || {};
    return {
      name: quoteFor.company || quoteFor.name || quote?.customerName || "",
      phone: quoteFor.phone || "",
      whatsapp: quoteFor.phone || "",
      email: quoteFor.email || "",
      address: quoteFor.address || "",
      source: "Quotation",
      notes: "",
    };
  }

  return {
    name: quote?.customerName || "",
    phone: "",
    whatsapp: "",
    email: "",
    address: "",
    source: "Quotation",
    notes: "",
  };
};
